import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Icons } from '../components/ui/Icon';
import { getQuoteOutcome } from '../services/quote-outcome';
import { projectHasCompletedQuote, quoteAwaitsHumanReview } from '../utils/project-stage';
import {
    matchesRunSheetSearch, HISTORY_CATEGORIES, countCategoryMatches,
    classifyProjectCategories, categoryLabel,
} from '../services/history-search';
import {
    RUN_SHEET_COLUMNS, COLUMN_SORT_KEY, DEFAULT_RUN_SHEET_SORT,
    loadColumnPrefs, saveColumnPrefs, readStoredColumnPrefs, normaliseColumnPrefs, visibleColumns, buildGridTemplate,
    reorderColumns, resizeColumn, resetColumnWidth, toggleColumnHidden, toggleColumnLocked,
    canDragColumn, defaultColumnPrefs, defaultDirFor, compareRows, getColumnDef,
    formatStrataPlan, formatQuoteRef, stripStrataNoise, formatCreatedDate,
    autoHiddenColumns, groupRunSheetRowsByJob, runSheetClientName, runSheetFirmName,
    type RunSheetColumnId, type RunSheetColumnPref, type RunSheetSort, type RunSheetJobCaption,
} from '../services/run-sheet-columns';
import { formatQuoteNumberLabel } from '../services/quote-jobs/quote-number';
import { kindTagTitle, kindTagWord, quoteKind } from '../services/quote-jobs/quote-kind';
import { formatFullDate } from '../services/quote-jobs/job-dates';
import '../styles/quote-job-kinds.css';
import { readFlagDefaultOn } from '../utils/feature-flags';
import { RUN_SHEET_COLUMNS_PREFERENCE, fetchUserPreference, saveUserPreference } from '../services/user-preferences';
import { STAGES } from '../config/brand';
import { useToast } from '../utils/use-toast';
import ReviewLabelPicker from '../components/run-sheet/ReviewLabelPicker';
import RunSheetViewMenu, {
    RunSheetFilterChips, type QuoteStatusFilterKey, type RunSheetViewLane,
} from '../components/run-sheet/RunSheetViewMenu';
import RunSheetMenu, { RunSheetPopover } from '../components/run-sheet/RunSheetMenu';
import RunSheetHeroThumbnail, {
    resolveRunSheetHeroCandidate,
} from '../components/run-sheet/RunSheetHeroThumbnail';
import {
    resolveTone, listReviewLabels, createReviewLabel, updateReviewLabel,
    deleteReviewLabel, setProjectReviewLabel,
    type ReviewLabel, type ReviewLabelTone,
} from '../services/review-labels';
import {
    type RunSheetPreviewDetailCache,
    upsertRunSheetPreviewDetailCache,
} from '../services/run-sheet-preview-detail-cache';
import { isRevisionProject, getRevisionBadgeText } from '../utils/quote-revision';
import { runSheetSearchTip } from '../services/demo-identity';

/** Lazy: the reference-file viewer pulls in the PDF and DOCX renderers, and the
 *  Run Sheet must not carry those in its bundle for a panel most opens never
 *  reach. Declared at module scope, never inside the component, or every render
 *  would mint a new lazy type and remount the viewer mid-read. */
const LazyReferenceFilesPanel = React.lazy(() => import('../components/ui/ReferenceFilesPanel'));

// ============================================================================
// DASHBOARD — "The Run Sheet"
// ----------------------------------------------------------------------------
// Rebuilt from the ground up 2026-07-27 (operator request).
//
// WHY THIS SHAPE
// Customer Zero runs 111 projects of which 2 have ever been decided and ~105
// have not moved in three weeks. There is no business intelligence to report:
// every ratio on the previous dashboard was arithmetic on n=2 or a clamp
// artifact (the pressure gauge computed 50*log2(1+pipeline/won) = 359 and
// pinned at 100 — a constant, not a metric). Meanwhile the page collapsed the
// six states `getQuoteOutcome` already returns into one word, "pending", and
// so could not tell a half-built draft from a quote sent five weeks ago.
//
// So this page does not report. It dispatches. Every project lands in exactly
// one lane by what it needs next, each lane is a filter, and every row carries
// the action that moves it forward. All of it derives from the `projects` prop
// the page already receives — no new endpoints, no new fetches.
//
// STANDING DESIGN RULES HONOURED (see memory: design_preferences_peter)
//  · Light surfaces. The all-dark "Instrument Cluster" (dashboard-v4.css) was
//    rejected 2026-04-20 and its stylesheet is dead; do not revive it.
//  · Never waste vertical space. 34px rows, 10-14px card padding, 3px button
//    padding. Whole page fits 1440x900 above the fold (the old one overflowed
//    by ~250px).
//  · Rule 12a: no thin coloured left-edge accents. Lane colour is carried by a
//    top-edge stripe and by the chip itself.
//  · Whole dollars, ui-monospace, tabular-nums.
//  · Fixed-width columns so values line up across rows; the documented
//    responsive hiding order in components.css is preserved via .qm-dash-col-*.
//  · No idle motion, no greeting, no clock (the 1s clock drove ~20 unmemoised
//    array traversals per second).
// ============================================================================

type AnyProject = Record<string, any>;

/** Lane keys. A project belongs to exactly one of the first four. */
/** Exported so the Analysis model reuses this exact union rather than restating
 *  it: a second copy would let the two pages drift apart on what a lane is. */
export type LaneKey = 'finish' | 'send' | 'waiting' | 'decided' | 'history';

interface Lane {
    key: LaneKey;
    label: string;
    shortLabel: string;
    hint: string;
    /** Verb shown on the row action button for this lane. */
    action: string;
    icon: keyof typeof Icons;
    /** CSS custom-property colour token. */
    tone: string;
}

const LANES: Lane[] = [
    { key: 'finish',  label: 'Needs finishing', shortLabel: 'Finishing', hint: 'Started but no quote produced yet',        action: 'Resume',    icon: 'Tool',        tone: 'var(--rs-tone-finish)' },
    { key: 'send',    label: 'Ready to send',   shortLabel: 'Ready',     hint: 'Quote is built and has not gone out',      action: 'Open quote', icon: 'Send',       tone: 'var(--rs-tone-send)' },
    { key: 'waiting', label: 'Awaiting answer', shortLabel: 'Awaiting',  hint: 'Sent to the client, no decision recorded', action: 'Follow up', icon: 'Clock',       tone: 'var(--rs-tone-waiting)' },
    { key: 'decided', label: 'Decided',         shortLabel: 'Decided',   hint: 'Won, lost or expired',                     action: 'Open',      icon: 'CheckCircle', tone: 'var(--rs-tone-decided)' },
    // Materialised archive quotes (project_type='history'): real past quotes
    // imported from the corpus, NOT live work. Own lane so they never mix
    // with the four operational lanes.
    { key: 'history', label: 'History',         shortLabel: 'History',   hint: 'Imported historical quotes — not live jobs', action: 'Open',    icon: 'Folder',      tone: 'var(--rs-tone-history)' },
];

const DAY = 86400000;

/** Ex-GST value, normalised for the project's GST mode. Mirrors the previous
 *  dashboard's contract exactly so headline figures do not shift on release.
 *  Exported: AnalysisPage aggregates with the same contract so the two pages
 *  can never disagree about a project's value. */
export function quoteValueExGst(q: AnyProject): number {
    const scope = q?.quoteData?.scope || q?.scope || [];
    if (!Array.isArray(scope)) return 0;
    const raw = scope.reduce((sum: number, item: any) => {
        const n = parseFloat(String(item?.amount ?? '0').replace(/,/g, ''));
        return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    const incGst = q?.quoteData?.isGstInclusive ?? q?.scopeGstInclusive ?? true;
    return incGst ? raw / 1.1 : raw;
}

/** Inc-GST value — the number actually printed on the quote the client receives,
 *  and what the run sheet's VALUE column shows since 2026-08-08.
 *
 *  Deliberately a SIBLING of quoteValueExGst rather than a replacement: that
 *  function is the shared contract AnalysisPage aggregates through, and changing
 *  it would move every historical figure on that page. The two surfaces now show
 *  different things ON PURPOSE and each says which, instead of one silently
 *  assuming. Same raw sum, same per-project GST mode, opposite direction. */
export function quoteValueIncGst(q: AnyProject): number {
    const scope = q?.quoteData?.scope || q?.scope || [];
    if (!Array.isArray(scope)) return 0;
    const raw = scope.reduce((sum: number, item: any) => {
        const n = parseFloat(String(item?.amount ?? '0').replace(/,/g, ''));
        return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    const incGst = q?.quoteData?.isGstInclusive ?? q?.scopeGstInclusive ?? true;
    return incGst ? raw : raw * 1.1;
}

/** The timestamp that best describes "when did this last actually move".
 *
 *  DELIBERATELY NEVER `updated_at`. Verified against live Customer Zero data
 *  2026-07-27: all 109 cached projects carried an `updatedAt` inside the same
 *  30-second window, because the Dexie sync rewrites it on every hydrate. It is
 *  a cache-write timestamp, not a business event, and using it made every row
 *  in every lane read "today" — destroying the entire age signal. `created_at`
 *  is genuine and well spread (65 rows at 22-60 days, 41 beyond 60), and the
 *  pre-existing pipeline-aging cohorts on the old dashboard used it for exactly
 *  this reason.
 *
 *  So: prefer a real business event where one is recorded, otherwise fall back
 *  to when the job arrived. For an unfinished job "45 days old" is the honest
 *  reading anyway — it came in 45 days ago and still has no quote.
 */
export function lastMovedAt(q: AnyProject, lane: LaneKey): number {
    const created = q?.createdAt || q?.created_at;
    const pick = lane === 'waiting'
        ? (q?.quoteStatusChangedAt || q?.quote_status_changed_at || q?.emailSentAt || q?.email_sent_at)
        : lane === 'decided'
            ? (q?.quoteStatusChangedAt || q?.quote_status_changed_at
                || q?.aiContext?.wonAt || q?.aiContext?.lostAt || q?.wonAt || q?.lostAt)
            : null;
    const t = pick || created;
    const ms = t ? new Date(t).getTime() : NaN;
    return Number.isFinite(ms) ? ms : 0;
}

/** Plain-English caption for the active ordering, so the sheet says what it did.
 *  Since Lucid 009 the sort control's own label carries it (sortLabel below),
 *  because the caption band above the sheet that used to print it is gone. */
const SORT_CAPTION: Partial<Record<string, { asc: string; desc: string }>> = {
    created: { desc: 'Every project, newest first', asc: 'Every project, oldest first' },
    moved: { desc: 'Every project, most recently moved first', asc: 'Every project, longest untouched first' },
    value: { desc: 'Every project, highest value first', asc: 'Every project, lowest value first' },
    title: { asc: 'Every project, job A-Z', desc: 'Every project, job Z-A' },
    sp: { asc: 'Every project, by strata plan', desc: 'Every project, by strata plan (reversed)' },
    qr: { asc: 'Every project, by quote request', desc: 'Every project, by quote request (reversed)' },
    quoteNo: { asc: 'Every project, by Quote #', desc: 'Every project, by Quote # (reversed)' },
    client: { asc: 'Every project, client A-Z', desc: 'Every project, client Z-A' },
    address: { asc: 'Every project, address A-Z', desc: 'Every project, address Z-A' },
    stage: { desc: 'Every project, furthest along first', asc: 'Every project, earliest stage first' },
    label: { asc: 'Every project, by review label', desc: 'Every project, by review label (reversed)' },
};

/** The three orders the sort control offers, as the old Newest / Oldest /
 *  Value buttons did. A column-header sort is still possible, and the control
 *  then shows it as a fourth, ticked entry rather than pretending otherwise. */
const SORT_PRESETS: Array<{ id: string; label: string; sort: RunSheetSort }> = [
    { id: 'newest', label: 'Newest first', sort: { key: 'created', dir: 'desc' } },
    { id: 'oldest', label: 'Oldest first', sort: { key: 'created', dir: 'asc' } },
    { id: 'value', label: 'Highest value first', sort: { key: 'value', dir: 'desc' } },
];

/** "Newest first", "Job A-Z", "By strata plan": the caption without its
 *  "Every project, " lead, which is what the sort control shows. */
export function sortLabel(sort: RunSheetSort): string {
    const caption = SORT_CAPTION[sort.key]?.[sort.dir];
    if (!caption) return 'Custom order';
    const s = caption.replace(/^Every project, /, '');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// laneOf deliberately does NOT trust getQuoteOutcome's 'quoted' verdict. That
// branch infers a produced quote from `quote_number`, but RJ Bird assigns the
// quote number as the job reference the moment a project is created, so ~106
// of 109 drafts would masquerade as finished quotes ready to send. Verified
// against live Customer Zero data 2026-07-27: status is 'draft' on 99 of
// them. `projectHasCompletedQuote` is the canonical test and requires real
// structural evidence (or the server-computed quote_complete flag on lean
// summaries), so it cannot be fooled the same way.
export function laneOf(q: AnyProject): LaneKey {
    // Corpus archive exhibits never enter the operational lanes.
    if ((q?.projectType || q?.project_type) === 'history') return 'history';
    const outcome = getQuoteOutcome(q);
    if (outcome === 'won' || outcome === 'lost' || outcome === 'expired') return 'decided';
    if (outcome === 'sent') return 'waiting';
    // Real structural evidence only — see the quote-number trap note above.
    if (projectHasCompletedQuote(q)) return 'send';
    return 'finish';
}

const money0 = (n: number) => '$' + Math.round(n || 0).toLocaleString('en-AU');

/** Compact money for chips: $1.14M / $312k / $940. Keeps the lane strip on one line. */
function moneyCompact(n: number): string {
    const v = Math.round(n || 0);
    if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 2) + 'M';
    if (v >= 1_000) return '$' + Math.round(v / 1000) + 'k';
    return '$' + v;
}

/** "Stage 3 of 5 — Work Method". STAGES comes from untyped brand config, so the
 *  name lookup is guarded rather than indexed blind. */
function stageTitle(stage: number): string {
    const entry = (STAGES as any[] | undefined)?.[stage - 1];
    const name = entry && typeof entry.name === 'string' ? entry.name : '';
    // Colon, not an em-dash: UI copy never carries em/en-dashes (standing rule).
    return `Stage ${stage} of 5${name ? `: ${name}` : ''}`;
}

/** Split the constant job-reference prefix off a title so the part that
 *  differs between rows can carry the visual weight. "QUOTE RBB/BAW (0526)"
 *  renders with a muted "QUOTE " and a bold "RBB/BAW (0526)". Titles that
 *  don't match the convention render unchanged.
 *  Exported (with ageLabel/ageTone below) for the Tasks page: a bound task
 *  inherits its job's Run Sheet age pill, and a second copy of any of these
 *  would let the two surfaces drift apart on what an age means. */
export function splitTitlePrefix(title: string): { prefix: string; rest: string } {
    const m = title.match(/^(QUOTE\s+)(.+)$/);
    return m ? { prefix: m[1] as string, rest: m[2] as string } : { prefix: '', rest: title };
}

export function ageLabel(days: number): string {
    if (days <= 0) return 'today';
    if (days === 1) return '1d';
    if (days < 100) return days + 'd';
    return Math.floor(days / 30) + 'mo';
}

/** Age severity drives the pill colour. Deliberately blunt: a builder should be
 *  able to spot the rot without reading a number. */
export function ageTone(days: number): 'ok' | 'warm' | 'cold' {
    if (days <= 7) return 'ok';
    if (days <= 21) return 'warm';
    return 'cold';
}

/**
 * The "Rev N" badge beside a row's QR, or null for none.
 *
 * The badge predates Jobs: it is how a legacy revision (a '-R1' or "(Rev 1)"
 * title, or the old folder fields) was shown. A Jobs revision and an imported
 * quote print their revision in the QUOTE # column ("RBB260905 Rev 1",
 * "Q-4471 Rev 2"), so the badge would say it twice, and in the 84px QR column
 * it ran into QUOTE # (live, 2026-09-25). It is dropped only when the QUOTE #
 * shows that same revision, so a legacy row, whose Quote # carries no
 * revision, keeps it.
 */
function qrRevisionBadge(row: { quoteNo?: string } & Record<string, any>): string | null {
    if (!isRevisionProject(row)) return null;
    const text = getRevisionBadgeText(row);
    if (!text) return null;
    const n = Number(text.replace(/^Rev\s*/i, ''));
    const shownInQuoteNo = Number.isInteger(n) && n > 0 && typeof row.quoteNo === 'string'
        && new RegExp(`(?<![a-z0-9])rev\\s*0*${n}(?!\\d)`, 'i').test(row.quoteNo);
    return shownInQuoteNo ? null : text;
}

/**
 * Highlight search query tokens within text for rapid spatial orientation (Pass 6).
 * Tokenizes query on whitespace, ignores case, and wraps matching text in <mark className="rs-mark-highlight">.
 */
export function HighlightMatch({ text, query }: { text: string | null | undefined; query: string }) {
    if (!text) return null;
    const q = (query || '').trim();
    if (!q) return <>{text}</>;
    const tokens = Array.from(new Set(q.split(/\s+/).filter(Boolean))).map((t) => t.toLowerCase());
    if (tokens.length === 0) return <>{text}</>;

    const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = String(text).split(regex);

    return (
        <>
            {parts.map((part, i) => {
                if (tokens.includes(part.toLowerCase())) {
                    return <mark key={i} className="rs-mark-highlight">{part}</mark>;
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </>
    );
}

/**
 * Kill switch for grouping the quotes of one Job under a caption (docs/job/
 * GOAL.md C4, D3, A17). Default ON. `localStorage.setItem(
 * 'qm_run_sheet_job_grouping', 'false')` renders the flat sheet exactly as it
 * was before Jobs existed; the Quote # column and search are not affected.
 */
export const RUN_SHEET_JOB_GROUPING_FLAG = 'qm_run_sheet_job_grouping';

/**
 * The caption above the quotes of one Job. It is NOT a project row: it cannot
 * be selected, previewed or opened, it has no hover state, and nothing counts
 * it. Every figure it shows is derived from the rows under it.
 *
 * Semantics. On the desktop grid it is a `row` holding one `gridcell` that
 * spans every column, because a grid's rowgroup may only own rows, and a
 * `separator` there would be an invalid child the reader skips. Inside that
 * cell the Job name is a level-2 `heading`, so heading navigation walks from
 * Job to Job in both layouts. On the phone the sheet is a region (or a group
 * in Select mode) of card buttons, and the same heading introduces the cards
 * that follow it.
 */
function RunSheetJobCaptionRow({
    caption, grid, colCount,
}: { caption: RunSheetJobCaption; grid: boolean; colCount: number }) {
    // The "QUOTE " prefix is dropped here for the same reason the JOB column
    // drops it: a word on every line distinguishes nothing.
    const title = splitTitlePrefix(caption.title).rest || caption.title || 'Untitled job';
    const meta: Array<{ text: string; kind: 'ident' | 'count' | 'more' }> = [];
    // The caption supplies its own "QR"/"SP" label, so a stored "QR56101" or
    // "Strata Plan 56101" loses its key first (only when digits follow).
    const qr = caption.qr ? caption.qr.replace(/^(?:quote\s+request|q\.?\s?r\.?)[\s.:#-]*(?=\d)/i, '') : '';
    const sp = caption.sp ? caption.sp.replace(/^(?:strata\s+plan|s\.?\s?p\.?)[\s.:#-]*(?=\d)/i, '') : '';
    if (qr) meta.push({ text: `QR ${qr}`, kind: 'ident' });
    if (sp) meta.push({ text: `SP ${sp}`, kind: 'ident' });
    meta.push({ text: `${caption.count} quotes`, kind: 'count' });
    if (caption.inOtherLanes) meta.push({ text: `${caption.inOtherLanes} more in other lanes`, kind: 'more' });
    if (caption.hiddenByFilters) {
        meta.push({ text: `${caption.hiddenByFilters} more hidden by the search or filters`, kind: 'more' });
    }
    return (
        <div className="rs-jobcap" role={grid ? 'row' : undefined} data-job-id={caption.jobId}>
            <div className="rs-jobcap__cell" role={grid ? 'gridcell' : undefined} aria-colspan={grid ? colCount : undefined}>
                <span className="rs-jobcap__heading" role="heading" aria-level={2}>
                    <span className="rs-jobcap__tag">Job</span>
                    <span className="rs-jobcap__title">{title}</span>
                </span>
                {meta.map((m) => (
                    <span key={m.text} className={`rs-jobcap__${m.kind}`}>{m.text}</span>
                ))}
            </div>
        </div>
    );
}

export default function DashboardPage({
    projects,
    projectsLoaded,
    projectsLoadError,
    onNavigate,
    onNewProject,
    onLoadProject,
    onOpenQuote,
    onOpenFileExplorer,
    onGenerateQuote,
    /** Optional: omit it and the row simply carries no delete control. */
    onDeleteProject,
    /** Optional: omit it and the bulk bar offers Clear but no Delete. */
    onDeleteProjects,
    /** Optional: `(projectId, patch)` after a confirmed review-label write, so
     *  the owner of the `projects` array can fold it in. Absent, the optimistic
     *  value still renders correctly for the rest of the session — the page
     *  degrades to a session-local truth rather than to a wrong one. */
    onProjectPatched,
    /** Optional: App's openSettings(tab). Absent, the picker simply carries no
     *  "Manage labels..." footer rather than offering a dead link. */
    onOpenSettings,
    /** Cross-page handoff: Analysis sends a lane, a search term and a sort. */
    pageNavOptions,
    /** The signed-in person. Their column layout is their own (2026-10-13). */
    user,
    /** Optional: async server re-pull of the summaries array (the owner holds
     *  the state, so this page can only ask). Present, the sheet header gains
     *  a Refresh control AND the page re-pulls once on mount — another
     *  device's new quote appears by visiting the Run Sheet, not by hard
     *  refresh (operator request 2026-08-14). Absent, nothing renders. */
    onRefreshProjects,
}: any) {
    const all: AnyProject[] = Array.isArray(projects) ? projects : [];
    const isLoading = !projectsLoaded && all.length === 0;

    // Operator direction 2026-07-30: "default to ALL view at all times". The
    // lane chips remain as filters; they just no longer pre-filter on arrival.
    // A deliberate cross-page handoff (Analysis -> Run Sheet) can still preset
    // a lane, because that IS the operator asking for one.
    const [lane, setLane] = useState<LaneKey | 'all'>(() => pageNavOptions?.lane || 'all');
    const [query, setQuery] = useState<string>(() => {
        if (typeof pageNavOptions?.query === 'string') return pageNavOptions.query;
        if (typeof pageNavOptions?.search === 'string') return pageNavOptions.search;
        return '';
    });
    // ── Review labels (migration 088) ────────────────────────────────────
    // Declared HERE, immediately under `query` and above EVERY memo, effect
    // dependency array and callback that reads them. Dependency arrays are
    // evaluated during RENDER, so a useState below its first consumer is a
    // temporal-dead-zone crash at component init rather than a type error you
    // notice later — this file has been bitten by exactly that three times, and
    // the notes on previewId / filesOpen / closePreview below say so.
    //
    // `labelIds` is the active FILTER (a set: OR within the facet), and the
    // sentinel '__none' means "unlabelled". `labels` is the tenant's definition
    // list, and `labelOverrides` is the optimistic layer: the rows memo depends
    // on `[all]`, which this page does not own, so a freshly-applied label has
    // nowhere to land until the parent refreshes its summaries.
    const [labelIds, setLabelIds] = useState<Set<string>>(() => new Set());
    const [statusFilters, setStatusFilters] = useState<Set<QuoteStatusFilterKey>>(() => new Set());
    const [labels, setLabels] = useState<ReviewLabel[]>([]);
    const [labelOverrides, setLabelOverrides] = useState<Record<string, string | null>>({});
    // Job grouping kill switch, read once per mount. Declared up here with the
    // other state for the same TDZ reason as labelIds above.
    const [jobGrouping] = useState<boolean>(() => readFlagDefaultOn(RUN_SHEET_JOB_GROUPING_FLAG));
    const { showToast, ToastBar } = useToast();
    // Focus opens the search helper panel; it closes on blur (unless the blur
    // is a click INSIDE the panel), Escape, picking an example, or any click
    // outside the wrap. The click-outside listener is belt-and-braces over
    // blur: blur's relatedTarget is null for clicks on non-focusable surfaces
    // in some engines, and the panel must never strand itself open.
    const [searchHelperOpen, setSearchHelperOpen] = useState(false);
    const searchWrapRef = useRef<HTMLDivElement | null>(null);

    // ── Server refresh (operator request 2026-08-14) ─────────────────────
    // `refreshing` drives the button's disabled/busy state. Both the mount pull
    // and the button path leave a persistent inline failure state. A toast
    // alone is too easy to miss, and an outage must never look like an empty
    // tenant. Existing rows remain visible as the last known good list.
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState('');
    const runRefresh = async (silent: boolean) => {
        if (!onRefreshProjects) return;
        setRefreshing(true);
        try {
            await onRefreshProjects();
            setRefreshError('');
        } catch {
            setRefreshError('QuoteMaker could not load the latest projects.');
            if (!silent) showToast('Projects were not refreshed. Check the connection and try again.', 'error');
        } finally {
            setRefreshing(false);
        }
    };
    useEffect(() => {
        // Once per mount after App's boot read has settled. Starting this while
        // that read is still in flight creates a race where the older request
        // can fail after this one succeeds and wrongly mark fresh rows stale.
        // Later visits remount the page with projectsLoaded=true and reconcile.
        if (onRefreshProjects && projectsLoaded) void runRefresh(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!searchHelperOpen) return;
        const onDown = (e: MouseEvent) => {
            if (!searchWrapRef.current?.contains(e.target as Node)) setSearchHelperOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [searchHelperOpen]);

    // The label DEFINITIONS, fetched once per mount. Deliberately not gated on
    // the picker being opened: the compact filter still needs its checkbox
    // options before anything is clicked. A failure is swallowed —
    // the Run Sheet's job is dispatching projects, and it must not degrade into
    // an error page because an annotation vocabulary could not be reached. The
    // rows still carry the name and tone that rode in on the summary payload,
    // so an unreachable definition list costs the FILTER and the PICKER, not
    // the labels already on screen.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const rows = await listReviewLabels();
                if (!cancelled) setLabels(rows);
            } catch {
                if (!cancelled) setLabels([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);
    // Newest first by default (operator preference 2026-07-27): the freshest
    // work is what's on the operator's mind; the rot is one click away via
    // "Oldest" and stays loud through the age pills and the nav UNSENT nag.
    // Operator direction 2026-07-30: default ordering is DATE CREATED, newest
    // first. Note this is deliberately NOT the age pill's signal — that stays
    // lane-aware last-movement (staleness), which is why a CREATED column
    // exists to make the default order legible.
    const [sort, setSort] = useState<RunSheetSort>(DEFAULT_RUN_SHEET_SORT);

    // Column layout: order, width, visibility and lock. Each person's own
    // (Peter, 2026-10-13: "tied to the User. So that another user can set up
    // their ideal view and it won't affect other users"): saved on their
    // account, with this browser's copy under their own key so the sheet draws
    // at once. 'session' is App's placeholder for a token without a user id.
    const viewUserId: string | null = user?.id && user.id !== 'session' ? String(user.id) : null;
    const [columnPrefs, setColumnPrefs] = useState<RunSheetColumnPref[]>(() => loadColumnPrefs(undefined, viewUserId));
    // The person the layout on screen belongs to, whether they changed it
    // since it loaded, and the server save waiting for a burst of drags to end.
    const columnsUserRef = useRef<string | null>(viewUserId);
    const columnsEditedRef = useRef(false);
    const columnsSaveRef = useRef<{ timer: ReturnType<typeof setTimeout>; prefs: RunSheetColumnPref[] } | null>(null);
    const persistColumns = useCallback((next: RunSheetColumnPref[]) => {
        const userId = columnsUserRef.current;
        saveColumnPrefs(next, undefined, userId);
        columnsEditedRef.current = true;
        if (!userId) return;
        if (columnsSaveRef.current) clearTimeout(columnsSaveRef.current.timer);
        columnsSaveRef.current = {
            prefs: next,
            timer: setTimeout(() => {
                columnsSaveRef.current = null;
                void saveUserPreference(RUN_SHEET_COLUMNS_PREFERENCE, next);
            }, 600),
        };
    }, []);
    // A save still waiting when the page closes is sent then.
    useEffect(() => () => {
        const pending = columnsSaveRef.current;
        if (!pending) return;
        clearTimeout(pending.timer);
        columnsSaveRef.current = null;
        void saveUserPreference(RUN_SHEET_COLUMNS_PREFERENCE, pending.prefs);
    }, []);
    // The person's saved layout. Another person signing in gets their own.
    useEffect(() => {
        if (columnsUserRef.current !== viewUserId) {
            // The previous person's unsent save is dropped, never sent as this one.
            if (columnsSaveRef.current) {
                clearTimeout(columnsSaveRef.current.timer);
                columnsSaveRef.current = null;
            }
            columnsUserRef.current = viewUserId;
            columnsEditedRef.current = false;
            setColumnPrefs(loadColumnPrefs(undefined, viewUserId));
        }
        if (!viewUserId) return undefined;
        let cancelled = false;
        void fetchUserPreference(RUN_SHEET_COLUMNS_PREFERENCE).then((saved) => {
            if (cancelled || !saved.ok || columnsUserRef.current !== viewUserId) return;
            // A change made while this loaded wins; it is already being saved.
            if (columnsEditedRef.current) return;
            if (Array.isArray(saved.value)) {
                const prefs = normaliseColumnPrefs(saved.value);
                setColumnPrefs(prefs);
                saveColumnPrefs(prefs, undefined, viewUserId);
                return;
            }
            // Nothing on the account yet: the layout this browser already shows
            // (the person's own copy, or the one everyone here shared) becomes
            // theirs, so nobody's view resets the day this ships.
            const local = readStoredColumnPrefs(undefined, viewUserId);
            if (local) {
                saveColumnPrefs(local, undefined, viewUserId);
                void saveUserPreference(RUN_SHEET_COLUMNS_PREFERENCE, local);
            }
        });
        return () => { cancelled = true; };
    }, [viewUserId]);
    // The Columns panel now opens from the overflow menu and hangs from its
    // button (Lucid 009), so the button's element is what anchors it and what
    // takes focus back when it closes.
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    const closeColumnMenu = useCallback(() => setColumnMenuOpen(false), []);
    const moreTriggerRef = useRef<HTMLButtonElement | null>(null);
    const viewTriggerRef = useRef<HTMLButtonElement | null>(null);
    // Phone only: the search folds behind an icon so the View button can have
    // the row. It stays open while a query exists, so an active filter is never
    // hidden behind the icon.
    const [phoneSearchOpen, setPhoneSearchOpen] = useState(false);
    const updateColumns = useCallback((next: RunSheetColumnPref[]) => {
        setColumnPrefs(next);
        persistColumns(next);
    }, [persistColumns]);
    // Auto-collapse. Measured on the SHEET, not the viewport: the sheet sits
    // beside a 320px rail, so it runs out of room long before any viewport
    // breakpoint fires. A ResizeObserver also catches the rail opening and
    // closing, which a media query cannot see at all.
    const [sheetWidth, setSheetWidth] = useState(0);
    const observerRef = useRef<ResizeObserver | null>(null);
    // CSS changes the sheet from an eleven-column grid to action cards at the
    // same breakpoint. ARIA must change with it: a hidden column header cannot
    // continue describing phone cards, and an actionable card must announce
    // the preview it opens. matchMedia keeps semantics aligned on live resize.
    const [phoneLayout, setPhoneLayout] = useState(() => {
        if (typeof window === 'undefined') return false;
        if (typeof window.matchMedia === 'function') return window.matchMedia('(max-width: 700px)').matches;
        return window.innerWidth <= 700;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (typeof window.matchMedia !== 'function') {
            const onResize = () => setPhoneLayout(window.innerWidth <= 700);
            window.addEventListener('resize', onResize);
            return () => window.removeEventListener('resize', onResize);
        }
        const queryList = window.matchMedia('(max-width: 700px)');
        const onChange = () => setPhoneLayout(queryList.matches);
        onChange();
        if (typeof queryList.addEventListener === 'function') {
            queryList.addEventListener('change', onChange);
            return () => queryList.removeEventListener('change', onChange);
        }
        queryList.addListener?.(onChange);
        return () => queryList.removeListener?.(onChange);
    }, []);
    // A CALLBACK ref, not useRef + useEffect([]). The effect form shipped first
    // and was DEAD: it reads sheetRef.current once, and under React 19's
    // development double-mount the ref can still be null at that moment, so the
    // observer was never attached and sheetWidth sat at 0 forever — every unit
    // test still passed, because the pure logic was never the broken part. A
    // callback ref fires when the node itself arrives and departs, so there is
    // no ordering left to get wrong.
    const sheetElRef = useRef<HTMLElement | null>(null);
    const sheetRef = useCallback((el: HTMLElement | null) => {
        sheetElRef.current = el;
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!el) return;
        // clientWidth, not contentRect: the sheet is an overflow-x container,
        // and clientWidth is the width actually offered to the grid once a
        // scrollbar has taken its gutter.
        //
        // ROW_INSET is subtracted because the grid does not get all of it: every
        // row carries `padding: 0 12px`, so the track list only ever has
        // clientWidth-24 to fit into. Without this the collapse believed it had
        // 24px more than it did and left the sheet fractionally side-scrolling
        // at the exact widths it was supposed to prevent that. (The other half
        // of the same old inaccuracy was the 10px column-gap, which is now
        // folded into each cell's padding so the track widths ARE the whole
        // story — see buildGridTemplate.)
        const ROW_INSET = 24;
        const measure = () => setSheetWidth(prev => {
            const w = Math.max(0, el.clientWidth - ROW_INSET);
            return Math.abs(prev - w) >= 1 ? w : prev;
        });
        // Measure FIRST and unconditionally. Without ResizeObserver the layout
        // then simply stops responding to later resizes instead of never
        // collapsing at all — and a context that delivers no observer
        // callbacks (a non-compositing/background page) still gets a correct
        // first measurement.
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        observerRef.current = ro;
    }, []);

    // ── Preview / select-mode state ──────────────────────────────────────
    // Declared HERE, above the column memos, not down with the rest of the
    // selection logic. `hiddenCols` reads selectMode, and a useState below its
    // first consumer is a temporal-dead-zone crash at component init rather
    // than a type error you notice later. tsc caught exactly that.
    const [previewId, setPreviewId] = useState<string | null>(null);
    /** True for one squeeze while the panel is leaving: it stays mounted so the
     *  grid track can close around it instead of it vanishing in a frame. */
    const [closing, setClosing] = useState(false);
    /** The reference-file viewer, layered over the preview panel. Declared HERE
     *  with the other preview state, not down beside the fetch that feeds it:
     *  the Escape handler below reads it in a dependency array, which is
     *  evaluated during RENDER, so a declaration under its first consumer is a
     *  temporal-dead-zone crash at component init. This file has been bitten by
     *  exactly that twice. */
    const [filesOpen, setFilesOpen] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);

    const autoHidden = useMemo(
        () => autoHiddenColumns(columnPrefs, sheetWidth),
        [columnPrefs, sheetWidth],
    );
    // TWO different kinds of "not shown", and the difference is the whole
    // reason the responsive squeeze can animate (operator, 2026-07-31).
    //
    //  omitted   — genuinely not rendered. Only the checkbox, which exists ONLY
    //              in Select mode; outside it, 32px spent advertising a mode
    //              nobody asked for. Entering Select mode is a deliberate,
    //              discrete act, so it is allowed to be a discrete change.
    //  collapsed — the auto-hide set. Still RENDERED, at a 0px track, so the
    //              track count does not change and the browser can interpolate
    //              grid-template-columns. Dropping the track instead makes the
    //              two templates different lengths, the transition is skipped,
    //              and every surviving column jumps in a single frame — which
    //              is exactly the jerk being fixed.
    const omittedCols = useMemo(
        () => (selectMode ? new Set<RunSheetColumnId>() : new Set<RunSheetColumnId>(['select'])),
        [selectMode],
    );
    const cols = useMemo(() => visibleColumns(columnPrefs, omittedCols), [columnPrefs, omittedCols]);
    // Active column resizing state and live alignment guide line
    const [resizingCol, setResizingCol] = useState<RunSheetColumnId | null>(null);
    const [resizeGuideX, setResizeGuideX] = useState<number | null>(null);

    const gridTemplate = useMemo(
        () => buildGridTemplate(columnPrefs, omittedCols, autoHidden),
        [columnPrefs, omittedCols, autoHidden],
    );

    // Click a header to sort by it; click the active one again to reverse.
    const sortByColumn = useCallback((id: RunSheetColumnId) => {
        const key = COLUMN_SORT_KEY[id];
        if (!key) return;
        setSort(prev => (prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: defaultDirFor(key) }));
    }, []);

    // Drag-to-reorder and drag-to-resize both live on the header row.
    const dragColRef = useRef<RunSheetColumnId | null>(null);
    const [dragOverCol, setDragOverCol] = useState<RunSheetColumnId | null>(null);

    const resizeStateRef = useRef<{
        id: RunSheetColumnId;
        startX: number;
        startW: number;
        minW: number;
        cellLeft: number;
        gripEl: HTMLElement;
        sheetEl: HTMLElement;
        lastWidth: number;
        rafId: number | null;
    } | null>(null);

    const columnPrefsRef = useRef(columnPrefs);
    columnPrefsRef.current = columnPrefs;
    const omittedColsRef = useRef(omittedCols);
    omittedColsRef.current = omittedCols;
    const autoHiddenRef = useRef(autoHidden);
    autoHiddenRef.current = autoHidden;

    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            const state = resizeStateRef.current;
            if (!state) return;
            e.preventDefault();

            const deltaX = e.clientX - state.startX;
            const newWidth = Math.max(state.minW, Math.min(1200, Math.round(state.startW + deltaX)));
            state.lastWidth = newWidth;

            const sheetEl = state.sheetEl;
            if (!sheetEl) return;

            if (state.rafId !== null) {
                cancelAnimationFrame(state.rafId);
            }

            state.rafId = requestAnimationFrame(() => {
                state.rafId = null;
                const tempPrefs = resizeColumn(columnPrefsRef.current, state.id, newWidth);
                const tempTemplate = buildGridTemplate(
                    tempPrefs,
                    omittedColsRef.current,
                    autoHiddenRef.current,
                    state.id,
                );
                sheetEl.style.setProperty('--rs-cols', tempTemplate);
                setResizeGuideX(state.cellLeft + newWidth);
            });
        };

        const onUp = (e: PointerEvent) => {
            const state = resizeStateRef.current;
            if (!state) return;

            if (state.rafId !== null) {
                cancelAnimationFrame(state.rafId);
                state.rafId = null;
            }

            try {
                state.gripEl.releasePointerCapture(e.pointerId);
            } catch {
                /* Safe fallback */
            }

            document.body.classList.remove('rs-resizing');
            state.sheetEl.style.removeProperty('--rs-cols');

            const finalWidth = state.lastWidth;
            const colId = state.id;
            resizeStateRef.current = null;
            setResizingCol(null);
            setResizeGuideX(null);

            setColumnPrefs(prev => {
                const next = resizeColumn(prev, colId, finalWidth);
                persistColumns(next);
                return next;
            });
        };

        const onCancel = (e: PointerEvent) => {
            const state = resizeStateRef.current;
            if (!state) return;
            if (state.rafId !== null) {
                cancelAnimationFrame(state.rafId);
                state.rafId = null;
            }
            try {
                state.gripEl.releasePointerCapture(e.pointerId);
            } catch {
                /* Safe fallback */
            }
            document.body.classList.remove('rs-resizing');
            state.sheetEl.style.removeProperty('--rs-cols');
            resizeStateRef.current = null;
            setResizingCol(null);
            setResizeGuideX(null);
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && resizeStateRef.current) {
                const state = resizeStateRef.current;
                if (state.rafId !== null) {
                    cancelAnimationFrame(state.rafId);
                    state.rafId = null;
                }
                try {
                    state.gripEl.releasePointerCapture((e as any).pointerId ?? 0);
                } catch {
                    /* Safe fallback */
                }
                document.body.classList.remove('rs-resizing');
                state.sheetEl.style.removeProperty('--rs-cols');
                resizeStateRef.current = null;
                setResizingCol(null);
                setResizeGuideX(null);
            }
        };

        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('keydown', onKeyDown);
            if (resizeStateRef.current?.rafId != null) {
                cancelAnimationFrame(resizeStateRef.current.rafId);
            }
        };
    }, [persistColumns]);

    const startResize = useCallback((e: React.PointerEvent, id: RunSheetColumnId) => {
        if (e.button !== 0) return;
        const def = getColumnDef(id);
        const pref = columnPrefsRef.current.find(p => p.id === id);
        if (!def || def.system || !pref || pref.locked) return;

        e.preventDefault();
        e.stopPropagation();

        const gripEl = e.currentTarget as HTMLElement;
        const cellEl = gripEl.closest('.rs-head__cell') as HTMLElement | null;
        const sheetEl = sheetElRef.current || (gripEl.closest('.rs-sheet') as HTMLElement | null);
        if (!cellEl || !sheetEl) return;

        try {
            gripEl.setPointerCapture(e.pointerId);
        } catch {
            /* Safe fallback */
        }

        const cellRect = cellEl.getBoundingClientRect();
        const sheetRect = sheetEl.getBoundingClientRect();
        const startW = Math.round(cellRect.width);
        const minW = def.minWidth;
        const cellLeft = cellRect.left - sheetRect.left + sheetEl.scrollLeft;

        resizeStateRef.current = {
            id,
            startX: e.clientX,
            startW,
            minW,
            cellLeft,
            gripEl,
            sheetEl,
            lastWidth: startW,
            rafId: null,
        };

        setResizingCol(id);
        setResizeGuideX(cellLeft + startW);
        document.body.classList.add('rs-resizing');
    }, []);

    const handleResetColumn = useCallback((id: RunSheetColumnId) => {
        const def = getColumnDef(id);
        if (!def || def.system) return;
        updateColumns(resetColumnWidth(columnPrefsRef.current, id));
    }, [updateColumns]);
    const searchRef = useRef<HTMLInputElement | null>(null);
    // Opening the phone search from its icon puts the caret in the field: the
    // tap was the operator asking to type.
    useEffect(() => {
        if (phoneSearchOpen) searchRef.current?.focus();
    }, [phoneSearchOpen]);

    // Cross-page navigation: consume pageNavOptions ONCE per navigation event.
    // The ref tracks the last consumed object identity, so a re-render cannot
    // re-apply the preset (which would fight the operator every time they
    // changed a lane) while a genuinely new handoff still lands. Same idiom as
    // AITrainingPage. Absent options leave today's defaults exactly as they are,
    // which is what makes this half of the Analysis handoff a no-op on its own.
    const lastConsumedNavRef = useRef<any>(null);
    useEffect(() => {
        if (pageNavOptions && pageNavOptions !== lastConsumedNavRef.current) {
            lastConsumedNavRef.current = pageNavOptions;
            if (pageNavOptions.lane) setLane(pageNavOptions.lane);
            if (typeof pageNavOptions.query === 'string') setQuery(pageNavOptions.query);
            // Cross-page handoffs still speak the old vocabulary ('newest' |
            // 'oldest' | 'value'), which now maps onto the created/value keys.
            if (pageNavOptions.sort) {
                const s = String(pageNavOptions.sort);
                setSort(s === 'value' ? { key: 'value', dir: 'desc' }
                    : s === 'oldest' ? { key: 'created', dir: 'asc' }
                        : { key: 'created', dir: 'desc' });
            }
        }
    }, [pageNavOptions]);

    // "/" focuses the filter, Escape clears it. Linear-style, costs no pixels.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = document.activeElement as HTMLElement | null;
            const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
            if (e.key === '/' && !typing) {
                e.preventDefault();
                // On a phone-width window the empty search is folded away, so
                // open it; the phoneSearchOpen effect focuses it once mounted.
                if (searchRef.current) searchRef.current.focus();
                else setPhoneSearchOpen(true);
            }
            if (e.key === 'Escape' && el === searchRef.current) { setQuery(''); searchRef.current?.blur(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // One pass over the projects array, memoised. The previous page recomputed
    // every derived value on a 1s clock tick; this one only recomputes when the
    // projects array itself changes.
    // Label id -> definition, so a row can resolve its own name and tone from
    // the live definition list rather than from whatever was denormalised onto
    // it. This is what makes a rename or a re-tone in Settings show up on the
    // sheet immediately instead of after the next full summary refresh.
    const labelById = useMemo(() => {
        const map = new Map<string, ReviewLabel>();
        for (const l of labels) map.set(l.id, l);
        return map;
    }, [labels]);

    const rows = useMemo(() => {
        const now = Date.now();
        return all.map((q) => {
            const key = laneOf(q);
            const moved = lastMovedAt(q, key);
            const days = moved ? Math.max(0, Math.floor((now - moved) / DAY)) : 0;
            const stage = Number(q?.currentStage ?? q?.current_stage ?? 1) || 1;
            const fd = q?.formData || q?.form_data || {};
            const createdRaw = q?.createdAt || q?.created_at;
            const createdMs = createdRaw ? new Date(createdRaw).getTime() : NaN;

            // ── Turnaround: request -> quote sent ────────────────────────
            // The SENT end, in the order the app itself trusts. getQuoteOutcome
            // calls a project 'sent' on email_sent/emailSentAt, so the email
            // timestamp is the primary; quote_status_changed_at is accepted
            // only when the status actually reached a sent-or-beyond state, or
            // it would date a draft's last edit as a send.
            const outcome = getQuoteOutcome(q);
            const statusChanged = q?.quoteStatusChangedAt || q?.quote_status_changed_at;
            const sentRaw = q?.emailSentAt || q?.email_sent_at
                || ((outcome === 'sent' || outcome === 'won' || outcome === 'lost' || outcome === 'expired')
                    ? statusChanged : null);
            const sentMs = sentRaw ? new Date(sentRaw).getTime() : NaN;
            // null, never 0: "not measurable" and "same day" are different
            // answers and only one of them is true here. Negative spans (a sent
            // date before the request date — clock skew or a back-dated import)
            // are discarded rather than shown as a negative turnaround.
            const respondDays = (Number.isFinite(createdMs) && Number.isFinite(sentMs) && sentMs >= createdMs)
                ? Math.max(0, Math.round((sentMs - createdMs) / DAY))
                : null;
            // The client field embeds the strata plan in at least six shapes
            // across the live corpus. SP now has its own column, so the client
            // column shows just the managing agent (operator, 2026-07-30).
            // The firm comes first: the linked client, then the requester
            // company, then the client company unless it is the printed
            // "The Owners of Strata Plan" (SPEC 2026-10-08 A2.3).
            const rawClient = runSheetClientName(q);

            // ── The operator's own mark (migration 088) ──────────────────
            // Three sources, in precedence order, and each one exists for a
            // reason:
            //   1. `labelOverrides` — an optimistic write from this session.
            //      The rows memo feeds on the `all` prop, which this page does
            //      not own, so without this a label the operator just applied
            //      would not appear until the parent refreshed its summaries.
            //   2. `labelById` — the LIVE definition list, so a rename or a
            //      re-tone in Settings is reflected on the sheet at once.
            //   3. The name/tone that rode in denormalised on the summary
            //      payload, which is the only thing available offline (the
            //      definition list has no Dexie mirror).
            const overridden = Object.prototype.hasOwnProperty.call(labelOverrides, String(q?.id));
            const labelId: string | null = overridden
                ? (labelOverrides[String(q?.id)] ?? null)
                : (q?.reviewLabelId ?? q?.review_label_id ?? null);
            const labelDef = labelId ? labelById.get(labelId) : undefined;
            const labelName = labelDef?.name
                ?? (overridden || !labelId ? '' : String(q?.reviewLabelName ?? q?.review_label_name ?? ''));
            const labelTone = resolveTone(
                labelDef?.tone ?? (labelId ? (q?.reviewLabelTone ?? q?.review_label_tone) : 'slate'),
            );

            const isEmailSent = Boolean(q?.emailSent ?? q?.email_sent);
            const isPdfMade = Boolean(q?.pdfGenerated ?? q?.pdf_generated);
            const quoteStatusRank = isEmailSent ? 3 : (isPdfMade ? 2 : (stage >= 5 ? 1 : 0));

            // ── Jobs (docs/job/GOAL.md A5, A17) ──────────────────────────
            // Server-owned columns from migration 120, camelCased by apiFetch.
            // The offline Dexie summary emits the same camelCase keys
            // (project-summary-job-fields.ts); the snake_case reads cover a raw
            // row passed in directly. quote_rev is 0-99; anything else reads as 0.
            const quoteRevRaw = Number(q?.quoteRev ?? q?.quote_rev ?? 0);
            const quoteRev = Number.isInteger(quoteRevRaw) && quoteRevRaw > 0 ? quoteRevRaw : 0;
            const sourceIssuer = String(q?.sourceIssuer ?? q?.source_issuer ?? '').trim();
            // Jobs UX (JOBS-UX-SPEC 1.9): the quote's kind and an open site
            // visit request, from slice A's list columns (camelCased by
            // apiFetch; the offline summary emits the same keys).
            const splitArea = String(q?.splitArea ?? q?.split_area ?? '').trim() || null;
            const importedRevRaw = q?.importedRev ?? q?.imported_rev;
            const importedRevNum = Number(importedRevRaw);
            const importedRev = importedRevRaw !== null && importedRevRaw !== undefined && importedRevRaw !== '' && Number.isInteger(importedRevNum)
                ? importedRevNum
                : null;
            const siteVisitRaw = q?.siteVisitRequestedAt ?? q?.site_visit_requested_at;
            const siteVisitRequestedAt = typeof siteVisitRaw === 'string' && siteVisitRaw.trim() ? siteVisitRaw : null;

            return {
                q,
                id: q?.id,
                lane: key,
                history: key === 'history',
                outcome,
                days,
                movedMs: moved || 0,
                createdMs: Number.isFinite(createdMs) ? createdMs : 0,
                sentMs: Number.isFinite(sentMs) ? sentMs : 0,
                respondDays,
                value: quoteValueIncGst(q),
                valueEx: quoteValueExGst(q),
                stage: Math.min(5, Math.max(1, stage)),
                quoteStatusRank,
                awaitsReview: quoteAwaitsHumanReview(q),
                title: String(q?.title || q?.name || 'Untitled project'),
                client: stripStrataNoise(rawClient),
                clientRaw: rawClient,
                sp: formatStrataPlan(fd?.strataplan),
                qr: formatQuoteRef(q?.quoteNumber ?? q?.quote_number),
                // One helper prints the Quote # on every surface (A5):
                // "RBB260905", "RBB260905 Rev 1", or another builder's number.
                quoteNo: formatQuoteNumberLabel({
                    qmQuoteNo: q?.qmQuoteNo ?? q?.qm_quote_no ?? null,
                    quoteRev,
                    sourceQuoteRef: q?.sourceQuoteRef ?? q?.source_quote_ref ?? null,
                }),
                sourceIssuer,
                kind: quoteKind({ quoteRev, splitArea, importedRev }),
                splitArea,
                siteVisitRequestedAt,
                quoteJobId: (q?.quoteJobId ?? q?.quote_job_id ?? null) as string | null,
                quoteJobTitle: String(q?.quoteJobTitle ?? q?.quote_job_title ?? ''),
                jobQr: formatQuoteRef(q?.quoteJobQuoteRequestNo ?? q?.quote_job_quote_request_no),
                jobSp: formatStrataPlan(q?.quoteJobStrataPlan ?? q?.quote_job_strata_plan),
                // A Job revision (quote_rev, A5) wins over the legacy restart
                // revision, which stays the fallback so old '-R1' rows keep
                // their badge exactly as before.
                revisionNumber: quoteRev > 0
                    ? quoteRev
                    : (q?.revisionNumber ?? q?.projectFolder?.revisionNumber ?? 0),
                revisionSuffix: q?.revisionSuffix ?? q?.projectFolder?.revisionSuffix ?? '',
                restartOfProjectId: q?.restartOfProjectId ?? q?.projectFolder?.restartOfProjectId ?? null,
                address: String(q?.address || fd?.address || ''),
                labelId,
                // `labelName` is what SORTS (see SortableRow.labelName) and what
                // renders. An id would sort the sheet by when a label happened
                // to be created, which means nothing to the person reading it.
                labelName,
                labelTone,

                // ── Preview-only fields ──────────────────────────────────
                // Everything below is for the detail panel, never the sheet.
                // All of it comes from the SUMMARY payload already in memory,
                // so opening a preview costs no request. The one thing that
                // does (the quote objective) is fetched lazily — see
                // previewDetail.
                heroImage: q?.selectedHeroImage ?? q?.selected_hero_image ?? null,
                photoCount: Number(q?.photoCount ?? q?.photo_count ?? 0) || 0,
                documentCount: Number(q?.documentCount ?? q?.document_count ?? 0) || 0,
                videoCount: Number(q?.videoCount ?? q?.video_count ?? 0) || 0,
                scopeCount: Array.isArray(q?.scope) ? q.scope.length : 0,
                quoteComplete: Boolean(q?.quoteComplete ?? q?.quote_complete),
                pdfGenerated: isPdfMade,
                emailSent: isEmailSent,
                unitNumber: String(fd?.unitNumber || ''),
                visitDate: String(fd?.visitDate || ''),
                // Who to ring. Site contact first: on a job that needs finishing
                // it is the person at the property, not the managing agent.
                contactName: String(fd?.siteContactName || fd?.attentionTo || ''),
                contactPhone: String(fd?.siteContactPhone || fd?.clientPhone || ''),

                // ── Client identity (preview panel only) ─────────────────
                // `clientCompany` is what was entered ON THE JOB, and on the
                // live corpus that is usually the owners corporation ("The
                // Owners - Strata Plan 19180", "SP 70569") rather than the
                // agency. `requesterCompany` is the fallback the CRM linker
                // itself uses (use-project-persistence.ts:1318) and is filled
                // on 65 of 115 projects, so it is a real second source, not a
                // guess. Fill rates measured against the live DB 2026-07-31:
                // clientCompany 111/115, clientEmail 102/115, clientName
                // 111/115, company_id 111/115 with zero dangling rows.
                // Since 2026-10-08 (SPEC A2.3) the linked firm's name leads and
                // owners wording is never shown as the client.
                clientCompany: runSheetFirmName(q),
                clientName: String(fd?.clientName || ''),
                clientEmail: String(fd?.clientEmail || ''),
                // Casing is genuinely dual here and the fallback is NOT dead:
                // the API path delivers camelCase, but the offline Dexie
                // summary emits `company_id` as a real snake_case key on
                // purpose (data-service.js:449-453, "what the CRM consumers
                // read"). The repo's own accessor for this exact field reads
                // both (client-identity-match.ts:38-40).
                companyId: q?.companyId ?? q?.company_id ?? null,
            };
        });
        // `labelById` and `labelOverrides` join `all` here deliberately. It
        // costs one extra pass over at most 200 rows when a label is applied or
        // renamed, which is nothing — and the alternative (resolving the label
        // downstream in `visible`) would split the row's identity across two
        // memos and leave the filter predicate reading a field the row does not
        // carry. The rule this file actually cares about is unchanged: nothing
        // here recomputes on a clock tick.
    }, [all, labelById, labelOverrides]);

    // Keyed by a union rather than `string` so `noUncheckedIndexedAccess` sees
    // known properties instead of `T | undefined` on every read.
    const laneStats = useMemo(() => {
        const base: Record<LaneKey | 'all', { count: number; value: number }> = {
            all: { count: 0, value: 0 },
            finish: { count: 0, value: 0 },
            send: { count: 0, value: 0 },
            waiting: { count: 0, value: 0 },
            decided: { count: 0, value: 0 },
            history: { count: 0, value: 0 },
        };
        for (const r of rows) {
            base[r.lane].count += 1;
            base[r.lane].value += r.value;
            // History rows are imported records, not live work. They live ONLY
            // behind their own chip: "All" (and its search) means live projects,
            // otherwise 1,400+ historical quotes swamp every search (operator
            // 2026-08-05: "search projects excluding History").
            if (r.lane === 'history') continue;
            base.all.count += 1;
            base.all.value += r.value;
        }
        return base;
    }, [rows]);

    const statusCounts = useMemo(() => {
        let pdf = 0;
        let sent = 0;
        let unsent = 0;
        let nopdf = 0;
        const q = query.trim();
        for (const r of rows) {
            if (lane !== 'all' && r.lane !== lane) continue;
            if (lane === 'all' && r.lane === 'history') continue;
            if (labelIds.size && !labelIds.has(r.labelId ?? '__none')) continue;
            if (q && !matchesRunSheetSearch(r.q, q)) continue;
            if (r.pdfGenerated) pdf++;
            else nopdf++;
            if (r.emailSent) sent++;
            else unsent++;
        }
        return { pdf, sent, unsent, nopdf };
    }, [rows, lane, query, labelIds]);

    const visible = useMemo(() => {
        const q = query.trim();
        const filtered = rows.filter((r) => {
            if (lane !== 'all' && r.lane !== lane) return false;
            // "All" excludes history — see the laneStats comment. Search over
            // history is still possible by selecting the History chip first.
            if (lane === 'all' && r.lane === 'history') return false;
            // OR within the label facet, AND across facets — the same shape
            // `matchesActiveCategories` already uses. '__none' is the sentinel
            // for unlabelled, so "Unlabelled" is a real chip rather than a
            // separate mode.
            //
            // Label text is deliberately NOT folded into the search haystack:
            // a label name would then collide with scope wording, and a chip
            // would return rows it does not describe.
            if (labelIds.size && !labelIds.has(r.labelId ?? '__none')) return false;

            // Quote status (PDF made / sent) facet
            if (statusFilters.size > 0) {
                if (statusFilters.has('pdf') && !r.pdfGenerated) return false;
                if (statusFilters.has('sent') && !r.emailSent) return false;
                if (statusFilters.has('unsent') && r.emailSent) return false;
                if (statusFilters.has('nopdf') && r.pdfGenerated) return false;
            }

            // Shared Project History engine: token-AND over title / address /
            // client / SP / quote numbers / scope wording, with job-type
            // synonym expansion ("balcony" also finds balustrade jobs).
            return matchesRunSheetSearch(r.q, q);
        });
        return filtered.slice().sort((a, b) => compareRows(a, b, sort));
    }, [rows, lane, query, sort, labelIds, statusFilters]);

    // ── Jobs: the same rows, re-ordered under captions (C4, D3, A17) ─────
    // `visible` stays the flat, sorted SET, and everything that counts or
    // selects keeps reading it, so a caption can never change a number.
    // `displayRows` is only the ORDER the rows are drawn in: the quotes of one
    // Job pulled together after the first of them in the current sort. Shift-
    // click ranges and j/k walk this order, because it is the order on screen.
    // With the kill switch off it IS `visible`, so the flat sheet renders
    // exactly as it did before Jobs.
    const jobDisplay = useMemo(
        () => (jobGrouping ? groupRunSheetRowsByJob(visible, rows, lane) : null),
        [jobGrouping, visible, rows, lane],
    );
    const displayRows = jobDisplay ? jobDisplay.rows : visible;

    // ── Preview, and selection ONLY when deleting ────────────────────────
    // Operator 2026-07-30, second pass. The first cut made every row click a
    // selection and put a Delete on all 114 rows, so the sheet read as
    // permanently armed: "when you click on one of the projects, it's like I'm
    // selecting it, ready to delete". Corrected here.
    //   - A row click opens a PREVIEW. That is the default, and it is harmless.
    //   - Selection exists only inside an explicit Select mode, because the
    //     only reason to multi-select on this page is to delete.
    //   - Deleting one project happens from inside its preview, so you are
    //     always looking at the job before you can remove it.
    // (previewId / selectMode / selected are declared above the column memos —
    // hiddenCols consumes selectMode, and a state hook below its first consumer
    // is a temporal-dead-zone crash at init.)
    //
    // Shift-click needs an anchor. A ref, not state: it must not cause a render,
    // and it is read only inside the click handler that sets it.
    const anchorRef = useRef<string | null>(null);

    // Leaving Select mode drops the ticks with it. A selection that outlived
    // its mode would sit invisible and still be there on re-entry.
    const exitSelectMode = useCallback(() => {
        setSelectMode(false);
        setSelected(new Set());
        anchorRef.current = null;
    }, []);

    // ── Closing the panel, with something to look at ─────────────────────
    // Operator, 2026-07-31: "if I click the row again, the same row/project,
    // the right panel should minimise. Make a satisfying transition."
    //
    // The panel is conditionally rendered, so on close it used to be ripped out
    // of the DOM in the same frame the grid track started easing shut — the
    // track animated over an empty column and the panel itself just blinked
    // out. `closing` keeps it mounted for exactly one squeeze while the track
    // closes around it, so the panel is seen to leave.
    //
    // Declared HERE, above the Escape handler that calls it. A useCallback
    // referenced in an effect's dependency array is read during RENDER, so a
    // definition below its first consumer is a temporal-dead-zone crash at
    // component init, not a type error you find later. This file has been bitten
    // by exactly that before (see the previewId/selectMode note above).
    const closeTimerRef = useRef<number | null>(null);
    const cancelClose = useCallback(() => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setClosing(false);
    }, []);
    const closePreview = useCallback(() => {
        setPreviewId((current) => {
            if (!current) return current;
            setClosing(true);
            if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
            // Matches --rs-squeeze. A timer rather than transitionend because
            // grid-template-columns fires transitionend per animated property
            // and would need de-duping, and because a transition the operator
            // interrupts (by opening another row mid-close) never fires at all,
            // which would strand the panel mounted forever.
            closeTimerRef.current = window.setTimeout(() => {
                closeTimerRef.current = null;
                setClosing(false);
                setPreviewId(null);
            }, 280);
            return current;
        });
    }, []);
    /** Opening always wins over a close already in flight. */
    const openPreview = useCallback((id: string) => {
        cancelClose();
        setPreviewId(id);
    }, [cancelClose]);
    // A pending close must not outlive the page.
    useEffect(() => () => {
        if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    }, []);

    // Selection is cleared whenever the visible SET changes identity (lane,
    // search, or the label filter). Keeping it would let "3 selected" refer to
    // rows scrolled out of existence by a filter — and then Delete would take
    // projects the operator could no longer see. Sort order is deliberately NOT
    // a trigger: it reorders the same rows rather than changing which ones
    // exist. `labelIds` belongs in the first group, not the second.
    useEffect(() => {
        setSelected(prev => (prev.size ? new Set() : prev));
        anchorRef.current = null;
    }, [lane, query, labelIds, statusFilters]);

    // Escape backs out one layer at a time: the preview first, then Select
    // mode. Never both at once, or one key press undoes two decisions.
    useEffect(() => {
        if (!previewId && !selectMode && !filesOpen) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            // One layer per press, innermost first: the file viewer, then the
            // preview, then Select mode. Collapsing two at once would undo a
            // decision the operator did not ask to undo.
            if (filesOpen) setFilesOpen(false);
            else if (previewId) closePreview();
            else exitSelectMode();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [previewId, selectMode, filesOpen, exitSelectMode, closePreview]);

    // A project deleted elsewhere must not linger in the selection and
    // resurrect a stale id in the next bulk action.
    useEffect(() => {
        setSelected((prev) => {
            if (!prev.size) return prev;
            const live = new Set(visible.map(r => r.id));
            const next = new Set([...prev].filter(id => live.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [visible]);

    const toggleRow = useCallback((row: any, shiftKey: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            const anchor = anchorRef.current;
            if (shiftKey && anchor) {
                // The order on SCREEN, so a range under a Job caption is the
                // run of rows the operator actually dragged across.
                const ids = displayRows.map(r => r.id);
                const a = ids.indexOf(anchor);
                const b = ids.indexOf(row.id);
                if (a !== -1 && b !== -1) {
                    // A shift-range ADDS; it never unselects, which is what makes
                    // "click the first, shift-click the last" behave predictably.
                    for (let i = Math.min(a, b); i <= Math.max(a, b); i++) next.add(ids[i]!);
                    return next;
                }
            }
            if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
            anchorRef.current = row.id;
            return next;
        });
    }, [displayRows]);

    const allVisibleSelected = visible.length > 0 && visible.every(r => selected.has(r.id));
    const someVisibleSelected = !allVisibleSelected && visible.some(r => selected.has(r.id));
    const toggleAll = useCallback(() => {
        setSelected(prev => (visible.length && visible.every(r => prev.has(r.id))
            ? new Set()
            : new Set(visible.map(r => r.id))));
        anchorRef.current = null;
    }, [visible]);

    // A row click does one of two things, and which one is never ambiguous
    // because Select mode is a visible state you turned on yourself.
    // Outside Select mode the click TOGGLES: the same row again minimises the
    // panel, a different row swaps its contents without closing anything.
    const handleRowClick = useCallback((row: any, shiftKey: boolean) => {
        if (selectMode) { toggleRow(row, shiftKey); return; }
        if (previewId === row.id && !closing) closePreview();
        else openPreview(row.id);
    }, [selectMode, toggleRow, previewId, closing, closePreview, openPreview]);

    // ── Linear / Vim-style row keyboard navigation (Pass 5) ──────────────
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = document.activeElement as HTMLElement | null;
            const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
            if (typing) return;

            if (e.key === 'j' || e.key === 'ArrowDown') {
                e.preventDefault();
                if (displayRows.length === 0) return;
                setFocusedRowIndex((prev) => {
                    const next = prev === null ? 0 : Math.min(prev + 1, displayRows.length - 1);
                    const rowEl = document.querySelectorAll<HTMLElement>('.rs-table .rs-row')[next];
                    rowEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    return next;
                });
            } else if (e.key === 'k' || e.key === 'ArrowUp') {
                e.preventDefault();
                if (displayRows.length === 0) return;
                setFocusedRowIndex((prev) => {
                    const next = prev === null ? 0 : Math.max(prev - 1, 0);
                    const rowEl = document.querySelectorAll<HTMLElement>('.rs-table .rs-row')[next];
                    rowEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    return next;
                });
            } else if (e.key === 'Enter' || e.key === 'o' || e.key === 'O') {
                if (focusedRowIndex !== null && displayRows[focusedRowIndex]) {
                    e.preventDefault();
                    handleRowClick(displayRows[focusedRowIndex], false);
                }
            } else if (e.key === 'x' || e.key === 'X' || (e.key === ' ' && !typing)) {
                if (focusedRowIndex !== null && displayRows[focusedRowIndex]) {
                    e.preventDefault();
                    const r = displayRows[focusedRowIndex];
                    if (!selectMode) setSelectMode(true);
                    toggleRow(r, false);
                }
            } else if (e.key === '1') {
                e.preventDefault();
                setLane('all');
            } else if (e.key === '2') {
                e.preventDefault();
                setLane('finish');
            } else if (e.key === '3') {
                e.preventDefault();
                setLane('send');
            } else if (e.key === '4') {
                e.preventDefault();
                setLane('waiting');
            } else if (e.key === '5') {
                e.preventDefault();
                setLane('decided');
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [displayRows, focusedRowIndex, handleRowClick, selectMode, toggleRow]);

    const previewRow = useMemo(
        () => (previewId ? visible.find(r => r.id === previewId) || null : null),
        [previewId, visible],
    );
    // Hero comes from the per-id detail cache (see the previewDetail effect):
    // it is a base64 data: URL, so carrying it on every summary row cost 81%
    // of the Run Sheet's payload to render an image for at most one project.
    // Null until the detail lands, which renders exactly like a hero-less job.
    // A previewed project that vanishes (deleted here, or by another tab) must
    // close its own drawer rather than leave a panel describing nothing.
    // Immediately, NOT through closePreview: there is no longer anything to
    // animate out, and playing a leave transition over a panel whose project
    // has just been deleted would keep a description of nothing on screen for
    // another beat. cancelClose also clears a close already in flight, so the
    // timer cannot fire later and fight a panel the operator has since reopened.
    useEffect(() => {
        if (previewId && !visible.some(r => r.id === previewId)) {
            cancelClose();
            setPreviewId(null);
        }
    }, [previewId, visible, cancelClose]);

    // The quote objective is the single most useful line in the panel — it is
    // the job in the operator's own words — and it is the ONE thing the
    // summary payload does not carry (it lives in quote_data.scopeObjective on
    // the full record). Fetched lazily per preview and kept in a small bounded
    // display cache. The cache stores only objective text and durable hero
    // identity. It never retains legacy inline image bytes, browser Blob ids,
    // Blob objects, or expiring signed URLs while an operator walks the list.
    const [previewDetail, setPreviewDetail] = useState<RunSheetPreviewDetailCache>(() => new Map());
    const [previewLoading, setPreviewLoading] = useState(false);
    // Hero: detail first, then the row. Declared HERE, below previewDetail —
    // above it is a TDZ ReferenceError at runtime (the same trap as
    // gotcha_useCallback_dep_array_tdz). The row's copy is null over the wire
    // (the server projection drops the base64 hero — it was 81% of the Run
    // Sheet payload for an image at most one project shows) but POPULATED
    // offline, where summaries come from local Dexie records that already hold
    // it and cost nothing to read, so offline keeps its instant thumbnail.
    const previewHeroCandidate = useMemo(() => (
        previewId
            ? (previewDetail.get(previewId)?.heroImage || resolveRunSheetHeroCandidate({
                selectedHeroImage: previewRow?.heroImage,
                quoteHeroImage: previewRow?.q?.quoteData?.header?.heroImage,
                photos: previewRow?.q?.photos,
            }))
            : null
    ), [previewDetail, previewId, previewRow]);
    useEffect(() => {
        if (!previewId || previewDetail.has(previewId)) return undefined;
        let cancelled = false;
        setPreviewLoading(true);
        (async () => {
            try {
                const { apiFetch } = await import('../services/data-service-api');
                const full = await apiFetch(`/projects/${previewId}`);
                const qd = full?.quoteData || {};
                if (cancelled) return;
                setPreviewDetail(prev => upsertRunSheetPreviewDetailCache(
                    prev,
                    previewId,
                    {
                        objective: qd.scopeObjective || qd.objective || qd.summary?.scopeObjective || '',
                        jobDescription: full?.jobDescription || '',
                        // The hero rides the FULL record now, not the summary.
                        // Cloud-safe projects persist an object reference
                        // (S3/blob/file metadata), while older projects may
                        // carry a data URL or photo id. Resolve every shape to
                        // the shared photo record; never pass the raw JSON
                        // object directly to <img src>.
                        heroImage: resolveRunSheetHeroCandidate({
                            selectedHeroImage: full?.selectedHeroImage ?? full?.selected_hero_image,
                            quoteHeroImage: qd?.header?.heroImage,
                            photos: full?.photos,
                        }),
                    },
                ));
            } catch {
                // A failed fetch must not poison this Project's cache with a
                // permanent blank hero. Leave the detail undefined so closing
                // and reopening the preview can retry after a transient 503 or
                // expired session. The effect does not spin here: neither of
                // its dependencies changes when the request fails.
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [previewId, previewDetail]);

    // ── Reference files for the OPEN project only ────────────────────────
    // Deliberately NOT folded into `previewDetail`: a documents array carries
    // extracted text (and sometimes a `pdfDataUrl`), and holding that for every
    // project an operator walks past is how a panel turns into a memory leak.
    // This holds ONE project's files and is replaced on every open, so the cost
    // is bounded no matter how far down the sheet you go.
    // It costs no extra request either — the effect above already fetches the
    // full record, which carries `documents`; it was simply throwing it away.
    const [previewFiles, setPreviewFiles] = useState<{ id: string | null; loading: boolean; documents: any[]; rfq: any | null }>(
        { id: null, loading: false, documents: [], rfq: null },
    );
    // Deps are `[previewId]` ONLY, and that is load-bearing. The first cut also
    // listed `previewFiles.id` and then assigned it inside the effect: the dep
    // changed, React ran the cleanup, the cleanup set `cancelled = true`, and
    // the fetch this very effect had just started threw its own result away.
    // The panel sat on "Looking for the reference files…" forever. An effect
    // must never depend on state it sets itself.
    useEffect(() => {
        if (!previewId) { setPreviewFiles({ id: null, loading: false, documents: [], rfq: null }); return undefined; }
        let cancelled = false;
        setPreviewFiles({ id: previewId, loading: true, documents: [], rfq: null });
        (async () => {
            try {
                const [{ apiFetch }, { resolveDocumentClassification }] = await Promise.all([
                    import('../services/data-service-api'),
                    import('../utils/document-classification'),
                ]);
                const full = await apiFetch(`/projects/${previewId}`);
                if (cancelled) return;
                const docs = Array.isArray(full?.documents) ? full.documents : [];
                const isRfq = (d: any) => resolveDocumentClassification(d)?.classification === 'rfq';
                const rfq = docs.find(isRfq) || null;
                // RFQ first: the operator's ask is "preview the reference files
                // STARTING WITH the Quote Request", and the viewer opens on
                // whatever is first in this list.
                const ordered = rfq ? [rfq, ...docs.filter((d: any) => d !== rfq)] : docs;
                setPreviewFiles({ id: previewId, loading: false, documents: ordered, rfq });
            } catch {
                if (!cancelled) setPreviewFiles({ id: previewId, loading: false, documents: [], rfq: null });
            }
        })();
        return () => { cancelled = true; };
    }, [previewId]);
    // Closing the preview closes the file viewer with it, or the viewer would
    // outlive the job it belongs to.
    useEffect(() => { if (!previewId) setFilesOpen(false); }, [previewId]);

    // ── "How would I find this job again?" (operator, 2026-07-31) ────────
    // The command bar's Search jobs box is the way back to a project, but
    // nothing ever told the operator which words work for a GIVEN job. This
    // derives them from the same engine the search itself runs on.
    //
    // THE RULE THAT MAKES IT TRUSTWORTHY: every candidate is run through
    // `matchesRunSheetSearch` against this very project before it is offered.
    // A chip that would return an empty sheet is never rendered, so the panel
    // cannot teach a search that does not work. That is not theoretical — the
    // existing category chips in the search helper already have this defect:
    // they set the query to the category LABEL, and "Concrete repair" is
    // token-AND'd, so a job whose scope says "render" (and never "repair")
    // matches the category but NOT its own chip. Verified 2026-07-31 by
    // running the module against a synthetic render-only job. Here the label
    // is only ever the caption; the query is whichever form actually matches.
    interface FindTerm { key: string; label: string; term: string; kind: 'trade' | 'id' | 'who' | 'where' }
    const previewFindAgain = useMemo<FindTerm[]>(() => {
        if (!previewRow) return [];
        const project = previewRow.q;
        const works = (term: string): string =>
            (term && matchesRunSheetSearch(project, term) ? term : '');
        const out: FindTerm[] = [];

        for (const id of classifyProjectCategories(project)) {
            const label = categoryLabel(id);
            // Label first (it is what the search helper's own chips show), then
            // the bare id, then the label's first word.
            const term = works(label) || works(id) || works(label.split(/\s+/)[0] || '');
            if (term) out.push({ key: `cat-${id}`, label, term, kind: 'trade' });
        }

        const add = (key: string, label: string, raw: string, kind: FindTerm['kind']) => {
            const term = works(String(raw || '').trim());
            if (term) out.push({ key, label, term, kind });
        };
        add('sp', 'Strata plan', previewRow.sp, 'id');
        add('qr', 'Quote request', previewRow.qr, 'id');
        add('client', 'Client', previewRow.client, 'who');

        // Street name, not the whole address: "12/45 Fern Street, Clovelly NSW
        // 2031" is a query nobody types. Take the part before the first comma
        // and drop the leading street number, which leaves "Fern Street". A
        // heuristic is acceptable here ONLY because of the verification rule
        // above — when it produces junk the chip simply does not appear.
        const street = String(previewRow.address || '')
            .split(',')[0]?.replace(/^[\s\d/-]+/, '').trim() || '';
        add('street', 'Street', street, 'where');

        return out;
    }, [previewRow]);

    const selectedRows = useMemo(() => visible.filter(r => selected.has(r.id)), [visible, selected]);
    const selectedValue = useMemo(
        () => selectedRows.reduce((sum, r) => sum + (Number(r.value) || 0), 0),
        [selectedRows],
    );

    // Helper-panel data. Chip counts come from the same classifier the search
    // uses, so a chip's number always equals what clicking it returns. The
    // typeahead ignores the four WORK lanes (a search is a whole-book question,
    // each hit names its lane) — but history is a different book, not a lane
    // of this one: it joins the pool ONLY while the History chip is active,
    // and then it IS the pool. Otherwise 1,400+ imported quotes dominate every
    // suggestion and chip count while the sheet below correctly shows none
    // (operator 2026-08-05: "search projects excluding History").
    const searchPool = useMemo(() => {
        const isHistory = (p: AnyProject) =>
            ((p as any)?.projectType || (p as any)?.project_type) === 'history';
        return lane === 'history' ? all.filter(isHistory) : all.filter((p) => !isHistory(p));
    }, [all, lane]);
    const categoryCounts = useMemo(() => countCategoryMatches(searchPool), [searchPool]);
    const searchMatches = useMemo(() => {
        const q = query.trim();
        if (!q) return [];
        return rows
            .filter((r) => (lane === 'history' ? r.lane === 'history' : r.lane !== 'history'))
            .filter((r) => matchesRunSheetSearch(r.q, q))
            .sort((a, b) => a.days - b.days)
            .slice(0, 6);
    }, [rows, query, lane]);

    const openProject = useCallback((row: any) => {
        // History rides the quote-open path (v5.50.6): an archived quote's
        // only interesting surface IS the produced quote, and this path ends
        // in a view-only editor.peek() — no merge, no generation. The
        // onLoadProject fallback would land the operator mid-pipeline on
        // Stage 1 as if it were a live job.
        if (row.lane === 'send' || row.lane === 'waiting' || row.lane === 'decided' || row.lane === 'history') {
            if (onOpenQuote) return onOpenQuote(row.q);
        }
        if (onLoadProject) return onLoadProject(row.q);
        return onNavigate?.('quotes');
    }, [onOpenQuote, onLoadProject, onNavigate]);

    const activeLane = LANES.find((l) => l.key === lane) || null;
    const hasLabelFilter = labelIds.size > 0;
    const hasStatusFilter = statusFilters.size > 0;

    // What the View menu lists: All, the four live lanes, then History, with
    // the same counts the lane chips carried (laneStats is unchanged).
    const viewLanes = useMemo<RunSheetViewLane[]>(() => [
        {
            key: 'all', label: 'All', shortLabel: 'All', icon: 'List',
            hint: 'Every live project (imported history has its own view)',
            count: laneStats.all.count,
        },
        ...LANES.map((l) => ({
            key: l.key, label: l.label, shortLabel: l.shortLabel, hint: l.hint,
            icon: l.icon, tone: l.tone, count: laneStats[l.key].count,
        })),
    ], [laneStats]);

    // The phone search row shows while it is open OR holds a query: an active
    // search must never be hidden behind the icon. On a desktop it always shows.
    const searchRowOpen = !phoneLayout || phoneSearchOpen || query.length > 0;
    const toggleSearchRow = () => {
        if (searchRowOpen && !query) { setPhoneSearchOpen(false); return; }
        if (searchRowOpen) { searchRef.current?.focus(); return; }
        setPhoneSearchOpen(true);
    };

    // The three sort presets, plus the column-header order when one is active
    // so the control never claims a preset the sheet is not in. Rendered in the
    // sort menu on a desktop and inside the overflow menu on a phone.
    const renderSortItems = (close: (returnFocus?: boolean) => void) => {
        const presetOn = SORT_PRESETS.some(p => p.sort.key === sort.key && p.sort.dir === sort.dir);
        return (
            <>
                {SORT_PRESETS.map((p) => {
                    const on = p.sort.key === sort.key && p.sort.dir === sort.dir;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            role="menuitemradio"
                            aria-checked={on}
                            tabIndex={-1}
                            className="rs-menu__item qm-inline-action-btn"
                            onClick={() => { setSort(p.sort); close(true); }}
                        >
                            <span className="rs-menu__tick" aria-hidden="true">{on && <Icons.Check size={13} />}</span>
                            <span className="rs-menu__label">{p.label}</span>
                        </button>
                    );
                })}
                {!presetOn && (
                    <button
                        type="button"
                        role="menuitemradio"
                        aria-checked
                        tabIndex={-1}
                        className="rs-menu__item qm-inline-action-btn"
                        title="Set by a column header. Click the header again to reverse it."
                        onClick={() => close(true)}
                    >
                        <span className="rs-menu__tick" aria-hidden="true"><Icons.Check size={13} /></span>
                        <span className="rs-menu__label">{sortLabel(sort)}</span>
                    </button>
                )}
            </>
        );
    };

    // ── Applying a label ─────────────────────────────────────────────────
    // One code path for the single-job case and the bulk case, because they
    // differ only in how many ids arrive. The write is optimistic: the operator
    // sees the chip land immediately and the request settles behind it, which
    // is the right trade for an annotation that is trivially re-applied.
    //
    // On failure the optimistic value is REVERTED (not left hopefully in
    // place). A label that says "Ready to send" on a job the server never
    // recorded is worse than no label at all — the whole point of the feature
    // is that the mark is trustworthy enough to filter a day's work by.
    //
    // Concurrency 4 mirrors how onDeleteProjects walks a selection. There is
    // deliberately NO bulk backend route in v1: the single PATCH is already the
    // narrowest possible write, and a second endpoint would be a second place
    // for the "never bump updated_at" rule to be forgotten.
    /**
     * `nameOverride` exists because a create-then-apply in the same tick still
     * holds the PRE-create `labels` closure, so `labels.find(...)` returns
     * undefined and the toast reads "Marked as ." with an empty name.
     *
     * `silent` lets a caller that owns a better sentence suppress the success
     * toast. The FAILURE toast always fires: a silent failure is never correct.
     */
    const applyLabelNamed = useCallback(async (
        ids: string[],
        labelId: string | null,
        opts: { nameOverride?: string; silent?: boolean } = {},
    ) => {
        if (!ids.length) return;
        const def = labelId ? labels.find(l => l.id === labelId) : null;
        const name = opts.nameOverride ?? (def?.name || '');

        const previous: Record<string, string | null> = {};
        for (const id of ids) {
            const row = rows.find(r => r.id === id);
            previous[id] = row?.labelId ?? null;
        }
        setLabelOverrides(prev => {
            const next = { ...prev };
            for (const id of ids) next[id] = labelId;
            return next;
        });

        const failed: string[] = [];
        const queue = ids.slice();
        const worker = async () => {
            for (;;) {
                const id = queue.shift();
                if (id === undefined) return;
                try {
                    await setProjectReviewLabel(id, labelId);
                    // Only the ID travels. The sheet resolves the name and the
                    // tone from the live definition list, so a later rename
                    // never has to be chased through the parent's array.
                    onProjectPatched?.(id, { reviewLabelId: labelId });
                } catch {
                    failed.push(id);
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(4, ids.length) }, worker));

        if (failed.length) {
            setLabelOverrides(prev => {
                const next = { ...prev };
                for (const id of failed) next[id] = previous[id] ?? null;
                return next;
            });
            showToast('Could not save the label. Nothing was changed.', 'error');
            return;
        }

        if (opts.silent) return;
        if (ids.length > 1) showToast(`Marked ${ids.length} jobs as ${name}.`, 'success');
        else if (!labelId) showToast('Label cleared.', 'success');
        else showToast(`Marked as ${name}.`, 'success');
    }, [labels, rows, onProjectPatched, showToast]);

    /** Every existing call site keeps its two-argument shape. */
    const applyLabel = useCallback(
        (ids: string[], labelId: string | null) => applyLabelNamed(ids, labelId),
        [applyLabelNamed],
    );

    /**
     * Create a label and put it on the job(s) in one gesture, so naming a mark
     * never costs a trip to Settings.
     *
     * Resolves rather than throws: the picker stays open on failure and renders
     * the message beside the field the operator is still looking at.
     */
    const createAndApply = useCallback(async (
        input: { name: string; tone: ReviewLabelTone },
        targetIds: string[],
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
        const jobs = (n: number) => (n === 1 ? 'this job' : `${n} jobs`);
        try {
            const created = await createReviewLabel({
                name: input.name,
                tone: input.tone,
                // labels.length, never the service default of 0: sort_order 0
                // sorts to the FRONT of both the picker list and the filter strip
                // (review-labels.js ORDER BY sort_order, lower(name)), so a new
                // label would silently reorder the strip under the operator.
                sortOrder: labels.length,
            });
            // BEFORE the assign, and load-bearing. The rows memo resolves a name
            // through labelById, built from `labels`; an override pointing at an
            // id absent from that map yields labelName '', so the chip renders
            // BLANK and sorts as unlabelled. The write succeeded and the sheet
            // would be saying it did not.
            setLabels(prev => [...prev, { ...created, usageCount: 0 }]);
            await applyLabelNamed(targetIds, created.id, { nameOverride: created.name, silent: true });
            showToast(targetIds.length
                ? `Created "${created.name}" and marked ${jobs(targetIds.length)}.`
                : `Created "${created.name}".`, 'success');
            return { ok: true };
        } catch (err: any) {
            // 409 is the case-insensitive unique index (migration 088). Almost
            // always another tab or another device since this page mounted,
            // because the picker already lists everything it knows about. The
            // intent is unambiguous, so recover by applying the label that does
            // exist and SAY so, rather than showing red for a name the operator
            // got right.
            if (err?.status === 409) {
                try {
                    const fresh = await listReviewLabels();
                    setLabels(fresh);
                    const match = fresh.find(l => l.name.toLowerCase() === input.name.trim().toLowerCase());
                    if (match) {
                        await applyLabelNamed(targetIds, match.id, { nameOverride: match.name, silent: true });
                        showToast(targetIds.length
                            ? `"${match.name}" already existed. Marked as ${match.name}.`
                            : `"${match.name}" already existed.`, 'success');
                        return { ok: true };
                    }
                } catch { /* fall through to the inline message */ }
                return { ok: false, message: err?.message || 'A label with that name already exists.' };
            }
            // Deliberately not "you need to be online": navigator.onLine is
            // banned as a warning source here, so the copy says only what is
            // actually known, which is that the POST did not land.
            return { ok: false, message: 'Could not create that label. Nothing was saved.' };
        }
    }, [labels, applyLabelNamed, showToast]);

    /**
     * Rename in place. The sheet resolves every chip's name through the live
     * definition list, so updating `labels` re-letters every row carrying it
     * with no second pass and no reload.
     */
    const renameLabel = useCallback(async (
        id: string, name: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
        try {
            const saved = await updateReviewLabel(id, { name });
            setLabels(prev => prev.map(l => (l.id === id ? { ...l, ...saved } : l)));
            showToast(`Renamed to "${saved.name}".`, 'success');
            return { ok: true };
        } catch (err: any) {
            // 409 is the case-insensitive unique index. The route writes that
            // sentence for the operator, so it is surfaced verbatim.
            return { ok: false, message: err?.message || 'Could not rename that label.' };
        }
    }, [showToast]);

    /**
     * Delete, and clean up after it. The route's ON DELETE SET NULL takes the
     * mark off the jobs server-side; everything below is the client catching up,
     * and each piece is here because leaving it out fails SILENTLY:
     *
     *  - out of `labels`, or the chips it lettered render blank.
     *  - out of `labelIds`, or a filter chip for a label that no longer exists
     *    stays armed and the sheet shows zero rows with no visible cause.
     *  - out of `labelOverrides`, or an optimistic entry keeps pointing at a
     *    dead id.
     *  - and the parent's own rows are patched, or the label reappears on the
     *    next render from the unpatched project envelope.
     */
    const deleteLabel = useCallback(async (
        id: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
        const doomed = labels.find(l => l.id === id);
        const affected = rows.filter(r => r.labelId === id).map(r => r.id);
        try {
            const res = await deleteReviewLabel(id);
            setLabels(prev => prev.filter(l => l.id !== id));
            setLabelIds(prev => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setLabelOverrides(prev => {
                const next = { ...prev };
                for (const rid of affected) next[rid] = null;
                return next;
            });
            for (const rid of affected) onProjectPatched?.(rid, { reviewLabelId: null });
            const cleared = res?.clearedProjects ?? affected.length;
            showToast(cleared
                ? `Deleted "${doomed?.name || 'label'}" and took it off ${cleared} job${cleared === 1 ? '' : 's'}.`
                : `Deleted "${doomed?.name || 'label'}".`, 'success');
            return { ok: true };
        } catch (err: any) {
            return { ok: false, message: err?.message || 'Could not delete that label.' };
        }
    }, [labels, rows, onProjectPatched, showToast]);

    /**
     * Re-read the definitions, so `usageCount` is live before a bin is armed.
     *
     * A label created in this session was appended locally with `usageCount: 0`
     * and never refreshed, so the delete confirm on a label the operator just
     * put on three jobs would offer a bare "Delete" and silently drop the number
     * it exists to show. Caught in a live run, not by a test.
     */
    const refreshLabels = useCallback(async () => {
        try { setLabels(await listReviewLabels()); } catch { /* keep what we have */ }
    }, []);

    // Deliberately `openSettings(tab)` and NOT handleNavigate with a
    // pageNavOptions payload. Settings does not read pageNavOptions at all: its
    // tab arrives through App's `settingsInitialTab` state, which openSettings
    // sets BEFORE navigating (and which also captures the page to come back
    // to). A pageNavOptions handoff would land on nothing and open the API tab.
    const openLabelSettings = useCallback(() => {
        onOpenSettings?.('labels');
    }, [onOpenSettings]);

    const projectsUnavailable = Boolean(projectsLoadError || refreshError);
    const hasVisibleRows = !isLoading && visible.length > 0;
    const useDesktopGrid = hasVisibleRows && !phoneLayout;

    return (
        <div className="rs-page">
            {/* ── Command bar: ONE row (Lucid 009, direction B) ───────────
                No greeting, no clock. Both were decoration, and the clock
                drove the whole page's derived state on a 1s tick.
                Peter, 2026-09-26, picked "One view menu": the six lane chips,
                the Status and Labels dropdowns and the sort and tools row
                under them became one row. Title and summary, a View button
                (the current lane and its count; the lanes, Status and Labels
                live in its menu), a removable chip per Status or Label filter
                that is set, the search, the sort, one overflow menu (Refresh,
                Select, Columns, Import folder) and New project. The row the
                chips sat on and the caption band above the sheet are gone,
                which gives the sheet back roughly 80px on a laptop.
                On a phone the View button takes the row beside a search icon,
                the overflow and New; the chips wrap underneath. */}
            <section className="rs-workspace-head" aria-label="Run sheet controls">
            <div className="rs-command" role="banner">
                <div className="rs-command__id">
                    <h1 className="rs-command__title">Run sheet</h1>
                    <span className="rs-command__sub">
                        {isLoading ? 'Loading' : `${laneStats.all.count} project${laneStats.all.count === 1 ? '' : 's'} · ${money0(laneStats.all.value)} inc GST`}
                        {refreshing && <span className="rs-command__busy"> · Refreshing…</span>}
                    </span>
                </div>

                <RunSheetViewMenu
                    lanes={viewLanes}
                    value={lane}
                    onPick={(key) => setLane(key as LaneKey | 'all')}
                    statusSelected={statusFilters}
                    onStatusChange={setStatusFilters}
                    statusCounts={statusCounts}
                    labels={labels}
                    labelSelected={labelIds}
                    onLabelsChange={setLabelIds}
                    compact={phoneLayout}
                    triggerRef={viewTriggerRef}
                />

                <RunSheetFilterChips
                    statusSelected={statusFilters}
                    onStatusChange={setStatusFilters}
                    labels={labels}
                    labelSelected={labelIds}
                    onLabelsChange={setLabelIds}
                    focusFallbackRef={viewTriggerRef}
                />

                {/* The guidance lives in a helper panel that opens on focus,
                    not in the placeholder (a long placeholder clips at narrow
                    widths) and not in a keycap chip (the pale "/" square read
                    as a blank blob, operator 2026-07-27). The panel hides the
                    moment a query exists so results are never covered. */}
                {searchRowOpen && (
                <div className="rs-search-wrap" ref={searchWrapRef}>
                    <label className="rs-search">
                        <Icons.Search size={15} aria-hidden="true" />
                        {/* qm-input-unthemed is the canonical opt-out from the
                            global input theme AND its :focus-visible inner
                            ring (components.css). Without it the ink border,
                            16px font and a second orange outline all land
                            inside this shell. The shell's :focus-within ring
                            carries the WCAG focus state instead. */}
                        <input
                            className="rs-search__input qm-input-unthemed"
                            ref={searchRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => setSearchHelperOpen(true)}
                            onBlur={(e) => {
                                const next = e.relatedTarget as Node | null;
                                if (!next || !searchWrapRef.current?.contains(next)) setSearchHelperOpen(false);
                            }}
                            onKeyDown={(e) => {
                                // Enter opens the top typeahead match: search,
                                // Enter, and you're inside the job.
                                if (e.key === 'Enter' && searchHelperOpen && searchMatches.length > 0) {
                                    e.preventDefault();
                                    openProject(searchMatches[0]);
                                }
                            }}
                            placeholder="Search jobs"
                            type="search"
                            role="searchbox"
                            aria-label="Search projects by job type, address, client, strata plan or quote number"
                            aria-expanded={searchHelperOpen}
                            aria-controls={searchHelperOpen ? 'run-sheet-search-help' : undefined}
                            spellCheck={false}
                        />
                        {/* The match count used to sit in the caption band over
                            the sheet, which is gone. It belongs beside the query
                            it counts. */}
                        {query && (
                            <span className="rs-search__count" aria-live="polite">
                                {visible.length} match{visible.length === 1 ? '' : 'es'}
                            </span>
                        )}
                        {query && (
                            <button type="button" className="rs-search__clear qm-inline-action-btn" onClick={() => setQuery('')} aria-label="Clear search">
                                <Icons.X size={12} />
                            </button>
                        )}
                    </label>

                    {searchHelperOpen && !query && (
                        <div id="run-sheet-search-help" className="rs-search-help" role="region" aria-label="What you can search">
                            <p className="rs-search-help__line">
                                Search by trade, address, client, strata plan or quote number.
                            </p>
                            <div className="rs-search-help__chips">
                                {/* Counts come from the same classifier the search
                                    runs, so the number always equals what the chip
                                    returns. Zero-count trades are omitted rather
                                    than shown disabled: a dead chip teaches
                                    nothing. */}
                                {HISTORY_CATEGORIES
                                    .filter((c) => (categoryCounts[c.id] || 0) > 0)
                                    .slice(0, 8)
                                    .map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="rs-search-help__chip qm-inline-action-btn"
                                            // mousedown would blur the input and close the
                                            // panel before click fires; suppress it so the
                                            // pick lands.
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => { setQuery(c.label.toLowerCase()); searchRef.current?.focus(); }}
                                        >
                                            {c.label}
                                            <span className="rs-search-help__count">{categoryCounts[c.id]}</span>
                                        </button>
                                    ))}
                            </div>
                            <p className="rs-search-help__tip">
                                {runSheetSearchTip()}
                            </p>
                        </div>
                    )}

                    {searchHelperOpen && query.trim() && (
                        <div id="run-sheet-search-help" className="rs-search-help" role="region" aria-label="Top matches">
                            {searchMatches.length === 0 ? (
                                <p className="rs-search-help__line rs-search-help__line--quiet">
                                    Nothing matches "{query.trim()}". Try a trade, an address, a client, or a quote number.
                                </p>
                            ) : (
                                <>
                                    <div className="rs-search-help__results">
                                        {searchMatches.map((r, i) => {
                                            const laneDef = LANES.find((l) => l.key === r.lane)!;
                                            return (
                                                <button
                                                    key={r.id}
                                                    type="button"
                                                    className="rs-search-hit qm-inline-action-btn"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => openProject(r)}
                                                    style={{ ['--lane-tone' as any]: laneDef.tone }}
                                                >
                                                    <span className="rs-search-hit__title">{r.title}</span>
                                                    <span className="rs-search-hit__where">
                                                        {[r.client, r.address].filter(Boolean).join(' · ')}
                                                    </span>
                                                    <span className="rs-search-hit__lane">{laneDef.label}</span>
                                                    <span className="rs-search-hit__value">{r.value < 1 ? '-' : money0(r.value)}</span>
                                                    {i === 0 && <kbd className="rs-search-hit__enter" aria-hidden="true">↵</kbd>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="rs-search-help__tip">
                                        Every match is also filtered in the sheet below. Enter opens the top one.
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </div>
                )}

                {/* Phone only: the search folds behind this icon so the View
                    button can have the row. */}
                {phoneLayout && (
                    <button
                        type="button"
                        className="rs-iconbtn rs-searchtoggle qm-inline-action-btn"
                        aria-label="Search projects"
                        aria-expanded={searchRowOpen}
                        data-on={query ? 'true' : 'false'}
                        onClick={toggleSearchRow}
                    >
                        <Icons.Search size={16} aria-hidden="true" />
                    </button>
                )}

                {/* The sort control names the order the sheet is in. On a
                    phone its choices move into the overflow menu. */}
                {!phoneLayout && (
                    <RunSheetMenu
                        label="Sort"
                        width={220}
                        align="end"
                        triggerClassName="rs-sortbtn qm-inline-action-btn"
                        triggerTitle="Change the order of the sheet"
                        triggerContent={(
                            <>
                                <Icons.ListOrdered size={13} aria-hidden="true" />
                                <span className="rs-sr">Sort: </span>
                                <span className="rs-sortbtn__label">{sortLabel(sort)}</span>
                                <Icons.ChevronDown size={12} aria-hidden="true" />
                            </>
                        )}
                    >
                        {(close) => renderSortItems(close)}
                    </RunSheetMenu>
                )}

                {/* One overflow menu for the tools that used to sit in the band
                    over the sheet. Select is the ONLY way into multi-select,
                    because the only reason to tick several rows on this page is
                    to delete them; the bulk bar that appears is what makes the
                    mode legible. Columns is desktop only: phone rows are cards
                    and ignore the column layout. */}
                <RunSheetMenu
                    label="More actions"
                    width={phoneLayout ? 260 : 220}
                    align="end"
                    triggerRef={moreTriggerRef}
                    triggerClassName="rs-iconbtn rs-more qm-inline-action-btn"
                    triggerAriaLabel="More actions"
                    triggerTitle="More actions"
                    triggerProps={{ 'aria-busy': refreshing || undefined }}
                    triggerContent={<Icons.MoreHorizontal size={18} aria-hidden="true" />}
                >
                    {(close) => (
                        <>
                            {phoneLayout && (
                                <>
                                    <div role="group" aria-label="Sort">
                                        <div role="none" className="rs-menu__head"><span aria-hidden="true">Sort</span></div>
                                        {renderSortItems(close)}
                                    </div>
                                    <div role="separator" className="rs-menu__sep" />
                                </>
                            )}
                            {onRefreshProjects && (
                                <button
                                    type="button"
                                    role="menuitem"
                                    tabIndex={-1}
                                    className="rs-menu__item rs-refresh qm-inline-action-btn"
                                    disabled={refreshing}
                                    aria-busy={refreshing}
                                    title="Pull the latest projects from the server (picks up work saved on other devices)"
                                    onClick={() => { close(true); void runRefresh(false); }}
                                >
                                    <Icons.RefreshCw size={13} aria-hidden="true" />
                                    {refreshing ? 'Refreshing…' : 'Refresh'}
                                </button>
                            )}
                            <button
                                type="button"
                                role="menuitemcheckbox"
                                aria-checked={selectMode}
                                tabIndex={-1}
                                className="rs-menu__item rs-selectmode qm-inline-action-btn"
                                title={selectMode ? 'Leave select mode' : 'Select several projects to delete'}
                                onClick={() => {
                                    close(true);
                                    if (selectMode) exitSelectMode();
                                    else { setSelectMode(true); closePreview(); }
                                }}
                            >
                                <Icons.CheckCircle size={13} aria-hidden="true" />
                                {selectMode ? 'Done selecting' : 'Select projects'}
                            </button>
                            {!phoneLayout && (
                                <button
                                    type="button"
                                    role="menuitem"
                                    tabIndex={-1}
                                    aria-haspopup="dialog"
                                    className="rs-menu__item qm-inline-action-btn"
                                    title="Choose columns"
                                    onClick={() => { close(false); setColumnMenuOpen(true); }}
                                >
                                    <Icons.Grid size={13} aria-hidden="true" />
                                    Columns
                                </button>
                            )}
                            {onOpenFileExplorer && (
                                <button
                                    type="button"
                                    role="menuitem"
                                    tabIndex={-1}
                                    className="rs-menu__item qm-inline-action-btn"
                                    onClick={() => { close(true); onOpenFileExplorer(); }}
                                >
                                    <Icons.FolderOpen size={13} aria-hidden="true" />
                                    Import folder
                                </button>
                            )}
                        </>
                    )}
                </RunSheetMenu>

                <button
                    type="button"
                    className="rs-btn rs-btn--primary rs-newbtn qm-inline-action-btn"
                    aria-label="New project"
                    onClick={() => (onNewProject ? onNewProject() : onNavigate?.('quotemaker'))}
                >
                    <Icons.Plus size={13} aria-hidden="true" />
                    <span className="rs-newbtn__long">New project</span>
                    <span className="rs-newbtn__short" aria-hidden="true">New</span>
                </button>
            </div>
            </section>

            {/* Column configurator, opened from the overflow menu. Phone rows
                are cards and deliberately ignore desktop visibility
                preferences, so the panel only exists while the desktop grid is
                active. */}
            {!phoneLayout && (
                <RunSheetPopover
                    open={columnMenuOpen}
                    anchorRef={moreTriggerRef}
                    onClose={closeColumnMenu}
                    label="Columns"
                    width={260}
                    align="end"
                    className="rs-colmenu__pop"
                >
                    <p className="rs-colmenu__hint">Drag a header to reorder · drag its edge to resize</p>
                    {columnPrefs.filter(p => !getColumnDef(p.id)?.system).map((p) => {
                        const def = getColumnDef(p.id)!;
                        return (
                            <div className="rs-colmenu__row" key={p.id}>
                                <label className="rs-colmenu__show">
                                    <input
                                        type="checkbox"
                                        className="qm-input-unthemed"
                                        checked={!p.hidden}
                                        onChange={() => updateColumns(toggleColumnHidden(columnPrefs, p.id))}
                                    />
                                    <span>{def.longLabel}</span>
                                </label>
                                <button
                                    type="button"
                                    className="rs-colmenu__lock qm-inline-action-btn"
                                    data-on={p.locked ? 'true' : 'false'}
                                    aria-pressed={p.locked ? 'true' : 'false'}
                                    onClick={() => updateColumns(toggleColumnLocked(columnPrefs, p.id))}
                                    title={p.locked ? 'Unlock: allow this column to be moved and resized' : 'Lock: freeze this column’s size and position'}
                                >
                                    {/* One glyph, two states: there is no Unlock
                                        icon in the set, and a dimmed padlock reads
                                        as "unlocked" more clearly than a second
                                        unfamiliar shape would. */}
                                    <Icons.Lock size={11} aria-hidden="true" />
                                </button>
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        className="rs-colmenu__reset qm-inline-action-btn"
                        onClick={() => updateColumns(defaultColumnPrefs())}
                    >
                        Reset to defaults
                    </button>
                </RunSheetPopover>
            )}

            {/* ── The run sheet ───────────────────────────────────────────── */}
            {/* The operator's column layout drives the ONE shared grid template
                every row (and the header) consumes, so columns stay aligned
                no matter how they are reordered, resized or hidden. */}
            {/* Bulk bar. Only exists while something is selected, so it costs no
                vertical space in the normal case — the page's standing rule is
                that every pixel earns its keep. It states the COUNT and the
                VALUE, because "delete 3 projects" and "delete $84k of quoted
                work" are different decisions. */}
            {selectMode && (
                <div className="rs-bulk" role="region" aria-label="Selection actions">
                    <span className="rs-bulk__count">
                        {selectedRows.length === 0 ? (
                            <span className="rs-bulk__hint">Tick the projects you want to delete. Shift-click for a range.</span>
                        ) : (
                            <>
                                <strong>{selectedRows.length}</strong> selected
                                <span className="rs-bulk__value">{moneyCompact(selectedValue)}</span>
                            </>
                        )}
                    </span>
                    <div className="rs-bulk__actions">
                        {/* The operator's stated motion is "process all the
                            quotes entirely, then mark them", so bulk labelling
                            is in v1 rather than deferred. It sits BEFORE Cancel
                            and Delete: it is the additive action in a bar
                            otherwise made of destructive ones.
                            No bulk backend route — this loops the same single
                            PATCH at concurrency 4, which is how onDeleteProjects
                            already walks a selection. */}
                        {/* Deliberately NOT gated on labels.length. A tenant with
                            no labels used to get no button here at all, which
                            made the create path unreachable in bulk mode — the
                            exact moment naming a mark is most useful. The picker
                            owns the empty case itself now. */}
                        <ReviewLabelPicker
                            labels={labels}
                            value={null}
                            heading={`Mark ${selectedRows.length} job${selectedRows.length === 1 ? '' : 's'}`}
                            onPick={(id) => applyLabel(selectedRows.map(r => r.id), id)}
                            onManage={onOpenSettings ? openLabelSettings : undefined}
                            onCreate={(input) => createAndApply(input, selectedRows.map(r => r.id))}
                            createTargetCount={selectedRows.length}
                            onRename={renameLabel}
                            onDelete={deleteLabel}
                            onRefreshLabels={refreshLabels}
                            triggerClassName="rs-bulk__btn qm-inline-action-btn"
                            triggerKind="label"
                            triggerTitle="Put a review label on every selected job"
                        >
                            <Icons.Tag size={12} aria-hidden="true" />
                            Label...
                        </ReviewLabelPicker>
                        <button
                            type="button"
                            className="rs-bulk__btn qm-inline-action-btn"
                            onClick={exitSelectMode}
                        >
                            Cancel
                        </button>
                        {onDeleteProjects && (
                            <button
                                type="button"
                                className="rs-bulk__btn rs-bulk__btn--danger qm-inline-action-btn"
                                disabled={selectedRows.length === 0}
                                /* The total is handed over, not recomputed on the
                                   other side. App.jsx has only the raw summary
                                   rows and would have to re-derive the same
                                   figure by a different route — which it did,
                                   and got 0 while this bar said $100k. One
                                   source, so the bar and the warning can never
                                   quote different numbers for the same set. */
                                onClick={() => onDeleteProjects(selectedRows.map(r => r.id), { totalValue: selectedValue })}
                            >
                                <Icons.Trash size={12} aria-hidden="true" />
                                Delete {selectedRows.length || ''} project{selectedRows.length === 1 ? '' : 's'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {projectsUnavailable && all.length > 0 && (
                <div className="rs-load-warning" role="alert">
                    <span className="rs-load-warning__icon" aria-hidden="true">
                        <Icons.AlertTriangle size={16} />
                    </span>
                    <span className="rs-load-warning__copy">
                        <strong>Latest projects unavailable</strong>
                        <span>Showing the last project list loaded on this device. Newer work may be missing.</span>
                    </span>
                    {onRefreshProjects && (
                        <button
                            type="button"
                            className="rs-load-warning__retry qm-inline-action-btn"
                            disabled={refreshing}
                            aria-busy={refreshing}
                            onClick={() => void runRefresh(false)}
                        >
                            <Icons.RefreshCw size={12} aria-hidden="true" />
                            {refreshing ? 'Trying again…' : 'Try again'}
                        </button>
                    )}
                </div>
            )}

            {/* Split, not an overlay. The sheet keeps its place so you can walk
                down the list and the preview swaps under you. Both panes carry
                their own floor width and a proportional share of the slack, and
                below the combined floor the preview STACKS BENEATH rather than
                vanishing — the Stage 4 rule, which exists because a fixed-width
                sidebar once squashed the left pane and then disappeared. */}
            {/* `closing` flips this to false a beat BEFORE the panel unmounts,
                which is what makes the grid track ease shut around a panel that
                is still there to be seen leaving. */}
            <div className="rs-split" data-preview={previewRow && !closing ? 'true' : 'false'}>
            <section
                ref={sheetRef}
                className="rs-sheet"
                role={useDesktopGrid ? 'grid' : (hasVisibleRows && phoneLayout ? (selectMode ? 'group' : 'region') : undefined)}
                aria-rowcount={useDesktopGrid ? visible.length + (jobDisplay?.captions.size ?? 0) + 1 : undefined}
                aria-colcount={useDesktopGrid ? cols.length : undefined}
                aria-multiselectable={useDesktopGrid && selectMode ? true : undefined}
                aria-busy={isLoading || refreshing}
                aria-label={activeLane ? activeLane.label : 'All projects'}
                style={{ ['--rs-cols' as any]: gridTemplate }}
            >
                {resizeGuideX !== null && (
                    <div
                        className="rs-resize-guide"
                        style={{ left: `${resizeGuideX}px` }}
                        aria-hidden="true"
                    />
                )}
                {/* Header row. Shares the same --rs-cols template as every data
                    row, so labels sit exactly above their column. It is also
                    where sorting, reordering and resizing are expressed. */}
                <div className="rs-head" role={useDesktopGrid ? 'row' : undefined}>
                    {cols.map((p) => {
                        const def = getColumnDef(p.id)!;
                        const sortKey = COLUMN_SORT_KEY[p.id];
                        const active = sortKey && sort.key === sortKey;
                        const draggable = canDragColumn(columnPrefs, p.id) && !resizingCol;
                        return (
                            <span
                                key={p.id}
                                className="rs-head__cell"
                                role={useDesktopGrid ? 'columnheader' : undefined}
                                data-col={p.id}
                                data-align={def.align || 'start'}
                                data-collapsed={autoHidden.has(p.id) ? 'true' : undefined}
                                data-drop={dragOverCol === p.id ? 'true' : 'false'}
                                aria-sort={useDesktopGrid ? (active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                                draggable={draggable}
                                onDragStart={(e) => {
                                    if (resizingCol) {
                                        e.preventDefault();
                                        return;
                                    }
                                    dragColRef.current = p.id;
                                    e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragOver={(e) => {
                                    if (resizingCol) return;
                                    if (!dragColRef.current || dragColRef.current === p.id) return;
                                    if (!canDragColumn(columnPrefs, p.id)) return;
                                    e.preventDefault();
                                    setDragOverCol(p.id);
                                }}
                                onDragLeave={() => setDragOverCol(c => (c === p.id ? null : c))}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (resizingCol) return;
                                    const from = dragColRef.current;
                                    dragColRef.current = null;
                                    setDragOverCol(null);
                                    if (from && from !== p.id) updateColumns(reorderColumns(columnPrefs, from, p.id));
                                }}
                                onDragEnd={() => { dragColRef.current = null; setDragOverCol(null); }}
                            >
                                {p.id === 'select' ? (
                                    <input
                                        type="checkbox"
                                        className="rs-check qm-input-unthemed"
                                        checked={allVisibleSelected}
                                        ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                                        onChange={toggleAll}
                                        disabled={!visible.length}
                                        aria-label={allVisibleSelected ? 'Clear selection' : `Select all ${visible.length} projects`}
                                        title={allVisibleSelected ? 'Clear selection' : 'Select all'}
                                    />
                                ) : sortKey ? (
                                    <button
                                        type="button"
                                        className="rs-head__sort qm-inline-action-btn"
                                        data-on={active ? 'true' : 'false'}
                                        onClick={() => sortByColumn(p.id)}
                                        title={`Sort by ${def.longLabel}`}
                                    >
                                        {def.label}
                                        {active && <i className="rs-head__arrow" data-dir={sort.dir} aria-hidden="true" />}
                                    </button>
                                ) : <span className="rs-head__label">{def.label}</span>}
                                {p.locked && <i className="rs-head__locked" aria-hidden="true" title="Locked" />}
                                {!def.system && !p.locked && (
                                    <i
                                        className="rs-head__grip"
                                        role="separator"
                                        aria-label={`Resize ${def.longLabel} (drag to resize, double-click to reset)`}
                                        title="Drag to resize, double-click to reset"
                                        data-resizing={resizingCol === p.id ? 'true' : undefined}
                                        draggable={false}
                                        onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onPointerDown={(e) => startResize(e, p.id)}
                                        onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); handleResetColumn(p.id); }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                )}
                            </span>
                        );
                    })}
                </div>

                <div className="rs-sheet__body" role={useDesktopGrid ? 'rowgroup' : undefined}>
                    {isLoading && Array.from({ length: 10 }).map((_, i) => (
                        <div className="rs-row rs-row--skeleton" key={`sk-${i}`} aria-hidden="true">
                            {/* Skeleton follows the operator's live column set, or
                                it would place shimmer bars under the wrong headers. */}
                            {cols.map((p) => (
                                <span className={`rs-cell-${p.id}`} key={p.id} data-collapsed={autoHidden.has(p.id) ? 'true' : undefined}>
                                    {p.id !== 'stage' && p.id !== 'select' && (
                                        <span className={`rs-skel rs-skel--${p.id === 'title' ? 'title' : p.id === 'value' ? 'value' : p.id === 'age' ? 'age' : 'client'}`} />
                                    )}
                                </span>
                            ))}
                        </div>
                    ))}

                    {!isLoading && visible.length === 0 && projectsUnavailable && all.length === 0 && (
                        <div className="rs-empty rs-empty--error" role="alert">
                            <span className="rs-empty__icon" aria-hidden="true">
                                <Icons.AlertTriangle size={22} />
                            </span>
                            <p className="rs-empty__title">Project list unavailable</p>
                            <p className="rs-empty__sub">
                                QuoteMaker could not confirm whether this business has projects. Check the connection and try again.
                            </p>
                            {onRefreshProjects && (
                                <div className="rs-empty__actions">
                                    <button
                                        type="button"
                                        className="rs-btn rs-btn--primary qm-inline-action-btn"
                                        disabled={refreshing}
                                        aria-busy={refreshing}
                                        onClick={() => void runRefresh(false)}
                                    >
                                        <Icons.RefreshCw size={13} aria-hidden="true" />
                                        {refreshing ? 'Trying again…' : 'Try again'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {!isLoading && visible.length === 0 && !(projectsUnavailable && all.length === 0) && (
                        <div className="rs-empty">
                            <span className="rs-empty__icon" aria-hidden="true">
                                {query || activeLane || hasLabelFilter || hasStatusFilter ? <Icons.Search size={20} /> : <Icons.QMQuotes size={22} />}
                            </span>
                            <p className="rs-empty__title">
                                {query
                                    ? 'Nothing matches that filter'
                                    : activeLane
                                        ? `Nothing ${activeLane.label.toLowerCase()}`
                                        : hasStatusFilter
                                            ? 'No projects match that status'
                                            : hasLabelFilter
                                                ? 'No projects match those labels'
                                                : 'No projects yet'}
                            </p>
                            <p className="rs-empty__sub">
                                {query
                                    ? 'Try a different trade, address, client, or quote number.'
                                    : activeLane
                                        ? 'This lane is clear. Pick another lane above.'
                                        : hasStatusFilter
                                            ? 'Clear or change the status filter to see other projects.'
                                            : hasLabelFilter
                                                ? 'Clear or change the selected labels to see other projects.'
                                                : 'Start a project or import a job folder to get going.'}
                            </p>
                            {(hasLabelFilter || hasStatusFilter) && (
                                <div className="rs-empty__actions">
                                    {hasStatusFilter && (
                                        <button
                                            type="button"
                                            className="rs-btn rs-btn--primary qm-inline-action-btn"
                                            onClick={() => setStatusFilters(new Set())}
                                        >
                                            Clear status filter
                                        </button>
                                    )}
                                    {hasLabelFilter && (
                                        <button
                                            type="button"
                                            className={`rs-btn ${hasStatusFilter ? '' : 'rs-btn--primary'} qm-inline-action-btn`}
                                            onClick={() => setLabelIds(new Set())}
                                        >
                                            Clear label filters
                                        </button>
                                    )}
                                </div>
                            )}
                            {!query && !activeLane && !hasLabelFilter && !hasStatusFilter && (
                                <div className="rs-empty__actions">
                                    <button type="button" className="rs-btn rs-btn--primary qm-inline-action-btn" onClick={() => (onNewProject ? onNewProject() : onNavigate?.('quotemaker'))}>
                                        <Icons.Plus size={13} aria-hidden="true" />
                                        New project
                                    </button>
                                    {onOpenFileExplorer && (
                                        <button type="button" className="rs-btn qm-inline-action-btn" onClick={() => onOpenFileExplorer()}>
                                            <Icons.FolderOpen size={13} aria-hidden="true" />
                                            Import folder
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!isLoading && displayRows.map((r, rowIdx) => {
                        const laneDef = LANES.find((l) => l.key === r.lane)!;
                        const tone = ageTone(r.days);
                        // A Job caption goes directly above the first of its
                        // quotes in the current order. It is a sibling, never
                        // an .rs-row, so row indices, j/k and every count skip it.
                        const jobCaption = jobDisplay?.captions.get(r.id);
                        // The row's name, in both modes. Quotes of one Job often
                        // share a title word for word, so the Quote # is what
                        // tells them apart, and Select mode (which exists to
                        // DELETE) is the last place two rows may sound the same.
                        const rowName = `${r.title}${r.quoteNo ? `, ${r.quoteNo}` : ''}`;
                        return (
                            <React.Fragment key={r.id}>
                            {jobCaption && (
                                <RunSheetJobCaptionRow caption={jobCaption} grid={useDesktopGrid} colCount={cols.length} />
                            )}
                            <div
                                key={r.id}
                                className="rs-row"
                                /* Desktop retains the data-grid relationship.
                                   Phone CSS renders action cards, so the role
                                   follows the visual interaction: preview button
                                   in normal use, checkbox while selecting. */
                                role={phoneLayout ? (selectMode ? 'checkbox' : 'button') : 'row'}
                                aria-selected={!phoneLayout && selectMode ? selected.has(r.id) : undefined}
                                aria-checked={phoneLayout && selectMode ? selected.has(r.id) : undefined}
                                aria-expanded={!selectMode ? previewId === r.id && !closing : undefined}
                                aria-controls={!selectMode ? 'run-sheet-project-preview' : undefined}
                                aria-label={selectMode
                                    ? `Select ${rowName}`
                                    : `${rowName}. ${previewId === r.id && !closing ? 'Close' : 'Open'} project preview`}
                                data-selected={selectMode && selected.has(r.id) ? 'true' : 'false'}
                                data-previewing={previewId === r.id && !closing ? 'true' : 'false'}
                                data-focused={focusedRowIndex === rowIdx ? 'true' : undefined}
                                data-has-query={query.trim() ? 'true' : undefined}
                                tabIndex={0}
                                /* Shift-click was drawing a border on the row.
                                   Chrome treats a modifier+click as
                                   keyboard-initiated, which satisfies
                                   :focus-visible and paints the 2px accent
                                   outline meant for keyboard navigation.
                                   Suppressing focus on the shift path removes
                                   it (and any drag-select) without touching the
                                   real keyboard ring, which still fires on Tab. */
                                onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                                onClick={(e) => handleRowClick(r, e.shiftKey)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleRowClick(r, e.shiftKey);
                                    }
                                }}
                                style={{ ['--lane-tone' as any]: laneDef.tone }}
                                data-job-member={jobDisplay?.memberIds.has(r.id) ? 'true' : undefined}
                                data-job-last={jobDisplay?.lastMemberIds.has(r.id) ? 'true' : undefined}
                            >
                                {/* Cells are emitted in the OPERATOR'S column order,
                                    not a hardcoded one, so a reorder moves the data
                                    and the header together on one shared template.
                                    A COLLAPSED column still emits its cell (the 0px
                                    track clips it) so the template and the cell list
                                    stay the same length mid-transition; the marker
                                    below then takes it out of the accessibility tree
                                    once the squeeze has finished, so a screen reader
                                    is never told about a column nobody can see. */}
                                {cols.map((p) => {
                                    const cell = ((): React.ReactElement | null => {
                                    switch (p.id) {
                                        // The checkbox is the DISCOVERABILITY of the
                                        // new click behaviour: without a visible
                                        // control, "clicking selects" is something
                                        // the operator has to discover by accident.
                                        // It does not stopPropagation — the row
                                        // handler already toggles, and swallowing
                                        // the event here would make the checkbox
                                        // the one part of the row that toggles
                                        // twice and therefore not at all.
                                        case 'select': return (
                                            <span className="rs-cell-select" key={p.id}>
                                                <input
                                                    type="checkbox"
                                                    className="rs-check qm-input-unthemed"
                                                    checked={selected.has(r.id)}
                                                    readOnly
                                                    tabIndex={-1}
                                                    aria-label={`Select ${rowName}`}
                                                />
                                            </span>
                                        );
                                        // Age + lane merged into one pill — a single
                                        // signal, not two disconnected chips. Measures
                                        // last MOVEMENT, which is why it is not the
                                        // same thing as the CREATED column.
                                        case 'age': return (
                                            <span className="rs-cell-age" key={p.id}>
                                                <span className="rs-age" data-tone={tone} title={`Last movement ${r.days} day${r.days === 1 ? '' : 's'} ago`}>
                                                    {ageLabel(r.days)}
                                                </span>
                                            </span>
                                        );
                                        // The constant "QUOTE " prefix is DROPPED, not
                                        // merely de-emphasised (operator, 2026-07-31:
                                        // "it doesn't need to show QUOTE at the front
                                        // of all the JOB names"). A word that appears
                                        // on every row distinguishes nothing and was
                                        // spending the widest column's opening
                                        // characters saying what the page already is.
                                        // The full untouched title is still on the
                                        // hover tooltip and in the preview panel, so
                                        // the record's real name is never lost.
                                        case 'title': return (
                                            <span className="rs-cell-title rs-title" key={p.id} title={r.title}>
                                                {r.history && (
                                                    <span
                                                        className="rs-history-chip"
                                                        title="Imported historical quote (corpus archive) — not a live job"
                                                    >HISTORY</span>
                                                )}
                                                {/* Jobs UX (lucid 007): one word, never a
                                                    digit; the Rev stays in QUOTE #. Every
                                                    quote under a JOB caption is tagged; a
                                                    row on its own only when it is revised or
                                                    split. A split row's area is already the
                                                    title text, so it reads "[SPLIT] Roof". */}
                                                {(jobDisplay?.memberIds.has(r.id) || r.kind === 'revised' || r.kind === 'split') && (
                                                    <span className="qm-kind-tag" data-kind={r.kind} title={kindTagTitle(r.kind, r.splitArea)}>
                                                        {kindTagWord(r.kind)}
                                                    </span>
                                                )}
                                                {r.siteVisitRequestedAt && (
                                                    <span
                                                        className="qm-visit-tag"
                                                        title={`A new site visit was requested with this revision${formatFullDate(r.siteVisitRequestedAt) ? ` on ${formatFullDate(r.siteVisitRequestedAt)}` : ''}.`}
                                                    >SITE VISIT</span>
                                                )}
                                                {/* Under a JOB caption, a quote titled
                                                    "<Job title> - Roof" shows "Roof": every
                                                    member repeating the Job's title left the
                                                    part that differs to be cut off. */}
                                                <HighlightMatch
                                                    text={jobDisplay?.memberTitles.get(r.id) || splitTitlePrefix(r.title).rest}
                                                    query={query}
                                                />
                                            </span>
                                        );
                                        // SP and QR are identifiers: tabular, so digits
                                        // line up down the column and a wrong one is
                                        // visible at a glance.
                                        case 'sp': return (
                                            <span className="rs-cell-sp rs-ident" key={p.id} title={r.sp ? `Strata plan ${r.sp}` : undefined}>
                                                <HighlightMatch text={r.sp} query={query} />
                                            </span>
                                        );
                                        // The legacy "Rev N" badge rides beside the
                                        // QR only when the QUOTE # column does not
                                        // already print that revision (see
                                        // qrRevisionBadge). Its layout is in the
                                        // stylesheet: it wraps under the QR inside
                                        // this column instead of running into QUOTE #.
                                        case 'qr': {
                                            const revBadge = qrRevisionBadge(r);
                                            return (
                                                <span className="rs-cell-qr rs-ident" key={p.id} title={r.qr ? `Quote request ${r.qr}` : undefined}>
                                                    <span className="rs-qr__num"><HighlightMatch text={r.qr} query={query} /></span>
                                                    {revBadge && <span className="rs-qr__rev">{revBadge}</span>}
                                                </span>
                                            );
                                        }
                                        // The Quote #, through the one helper every
                                        // surface uses (A5). Never ellipsised: a narrow
                                        // column wraps it inside the row instead, and
                                        // the tooltip names another builder's issuer.
                                        case 'quoteNo': return (
                                            <span
                                                className="rs-cell-quoteno rs-quoteno"
                                                key={p.id}
                                                title={r.quoteNo
                                                    ? `Quote # ${r.quoteNo}${r.sourceIssuer ? `, issued by ${r.sourceIssuer}` : ''}`
                                                    : undefined}
                                            >
                                                <HighlightMatch text={r.quoteNo} query={query} />
                                            </span>
                                        );
                                        // The operator's own mark. A plain <span>,
                                        // never a button: the global stylesheet
                                        // inflates every button to a 44px touch
                                        // target and paints a focus ring inside
                                        // it, both of which would wreck a 34px
                                        // row. Changing a label happens in the
                                        // preview panel, where the job is on
                                        // screen in front of you — the same
                                        // argument that moved every other row
                                        // control there.
                                        case 'label': return (
                                            <span className="rs-cell-label" key={p.id}>
                                                {r.labelName
                                                    ? (
                                                        <span className="rs-label" data-tone={r.labelTone} title={`Review label: ${r.labelName}`}>
                                                            {r.labelName}
                                                        </span>
                                                    )
                                                    : null}
                                            </span>
                                        );
                                        // Title carries the full unstripped client so
                                        // nothing is ever actually hidden from the
                                        // operator, only de-duplicated on screen.
                                        case 'client': return (
                                            <span className="rs-cell-client rs-client" key={p.id} title={r.clientRaw || undefined}>
                                                <HighlightMatch text={r.client} query={query} />
                                            </span>
                                        );
                                        case 'address': return (
                                            <span className="rs-cell-addr rs-addr" key={p.id} title={r.address || undefined}>
                                                <HighlightMatch text={r.address} query={query} />
                                            </span>
                                        );
                                        case 'created': return (
                                            <span className="rs-cell-created rs-ident" key={p.id} title={r.createdMs ? `Quote requested ${new Date(r.createdMs).toLocaleString('en-AU')}` : undefined}>
                                                {formatCreatedDate(r.createdMs)}
                                            </span>
                                        );
                                        // Days from request to sent. A dash means the
                                        // send was never recorded, which on today's
                                        // data is almost every row — see the column
                                        // definition for the measured fill rates. A
                                        // dash is the honest answer; a 0 would not be.
                                        case 'respond': return (
                                            <span
                                                className="rs-cell-respond rs-respond"
                                                key={p.id}
                                                data-tone={r.respondDays === null ? 'none' : ageTone(r.respondDays)}
                                                title={r.respondDays === null
                                                    ? 'No send recorded for this quote, so the turnaround cannot be measured'
                                                    : `Quoted ${r.respondDays} day${r.respondDays === 1 ? '' : 's'} after the request (sent ${new Date(r.sentMs).toLocaleDateString('en-AU')})`}
                                            >
                                                {r.respondDays === null ? '-' : `${r.respondDays}d`}
                                            </span>
                                        );
                                        // Dots carry the progress; the fraction makes
                                        // it exact. No "S" prefix — "S5" read as SS/55.
                                        case 'stage': return (
                                            <span className="rs-cell-stage rs-stage" key={p.id} title={stageTitle(r.stage)}>
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <i key={n} data-on={n <= r.stage ? 'true' : 'false'} aria-hidden="true" />
                                                ))}
                                                <span className="rs-stage__n">{r.stage}/5</span>
                                                {/* An AI-drafted quote nobody has opened
                                                    yet. Clears when opened at Stage 5. */}
                                                {r.awaitsReview && (
                                                    <span
                                                        className="rs-unreviewed"
                                                        title="Drafted by QuoteMaker — not yet reviewed. Open it at Stage 5 and save to clear this."
                                                    >
                                                        UNREVIEWED
                                                    </span>
                                                )}
                                            </span>
                                        );
                                        case 'quoteStatus': {
                                            const hasPdf = r.pdfGenerated;
                                            const hasSent = r.emailSent;
                                            const isDecided = r.outcome === 'won' || r.outcome === 'lost' || r.outcome === 'expired';
                                            const inStage5 = r.stage >= 5;

                                            if (!inStage5 && !hasPdf && !hasSent && !isDecided) {
                                                return (
                                                    <span className="rs-cell-status rs-status" key={p.id}>
                                                        <span className="rs-status-empty">-</span>
                                                    </span>
                                                );
                                            }

                                            return (
                                                <span className="rs-cell-status rs-status" key={p.id}>
                                                    <span
                                                        className="rs-milestone"
                                                        data-on={hasPdf ? 'true' : 'false'}
                                                        title={hasPdf ? 'PDF made' : 'PDF not yet generated'}
                                                    >
                                                        {hasPdf ? <Icons.Check size={10} aria-hidden="true" /> : null}
                                                        PDF MADE
                                                    </span>
                                                    <span
                                                        className="rs-milestone"
                                                        data-on={hasSent ? 'true' : 'false'}
                                                        title={hasSent ? 'Sent to client' : 'Not yet sent'}
                                                    >
                                                        {hasSent ? <Icons.Check size={10} aria-hidden="true" /> : null}
                                                        SENT
                                                    </span>
                                                    {isDecided && (
                                                        <span className="rs-milestone rs-milestone--outcome" data-outcome={r.outcome} title={`Quote is ${r.outcome}`}>
                                                            {r.outcome.toUpperCase()}
                                                        </span>
                                                    )}
                                                </span>
                                            );
                                        }
                                        // An unpriced draft shows a dash, not "$0". Ten
                                        // rows of $0 read as a broken column rather
                                        // than as "no scope priced yet".
                                        case 'value': return (
                                            <span className="rs-cell-value rs-value" key={p.id} data-zero={r.value < 1 ? 'true' : 'false'}>
                                                {r.value < 1 ? '-' : money0(r.value)}
                                            </span>
                                        );
                                        case 'valueEx': return (
                                            <span className="rs-cell-value rs-value" key={p.id} data-zero={(r.valueEx ?? 0) < 1 ? 'true' : 'false'}>
                                                {(r.valueEx ?? 0) < 1 ? '-' : money0(r.valueEx as number)}
                                            </span>
                                        );
                                        // There is no 'actions' case, and no actions
                                        // COLUMN, by operator direction 2026-07-31:
                                        // "the buttons Resume / Open quote / Generate
                                        // should not be there ... they should only
                                        // show when clicked and the right panel is
                                        // opened." Delete had already moved into the
                                        // preview for the same reason (a 114-row sheet
                                        // covered in controls reads as permanently
                                        // armed); this finishes the job. It also
                                        // removes the last way to spend AI money from
                                        // a chip you can hit without reading the row.
                                        default: return null;
                                    }
                                    })();
                                    if (!cell) return null;
                                    return React.cloneElement(cell, {
                                        role: useDesktopGrid ? 'gridcell' : undefined,
                                        ...(autoHidden.has(p.id) ? { 'data-collapsed': 'true' } : {}),
                                    } as any);
                                })}

                                {!phoneLayout && (
                                    <div className="rs-row-hover-bar" onClick={(e) => e.stopPropagation()}>
                                        {onOpenQuote && (
                                            <button
                                                type="button"
                                                className="qm-inline-action-btn rs-hover-btn"
                                                onClick={() => onOpenQuote(r.q)}
                                                title="Open quote in Stage 5"
                                                aria-label={`Open quote for ${r.title}`}
                                            >
                                                <Icons.FileText size={12} aria-hidden="true" />
                                                <span>Quote</span>
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="qm-inline-action-btn rs-hover-btn"
                                            onClick={() => {
                                                setPreviewId(r.id);
                                                setFilesOpen(true);
                                            }}
                                            title="Preview project files"
                                            aria-label={`Preview files for ${r.title}`}
                                        >
                                            <Icons.Folder size={12} aria-hidden="true" />
                                            <span>Files</span>
                                        </button>
                                    </div>
                                )}

                                {phoneLayout && (
                                    <div className="rs-card-actions" onClick={(e) => e.stopPropagation()}>
                                        {/* The Quote # on a phone card. The card's
                                            second line (address, age, stage) is
                                            already full at 360px and the QR is not
                                            shown on phones at all, so the number
                                            leads this strip, which has the free
                                            width. It wraps, never truncates. */}
                                        {r.quoteNo && (
                                            <span className="rs-card-quoteno" title={`Quote # ${r.quoteNo}`}>
                                                {r.quoteNo}
                                            </span>
                                        )}
                                        {r.address && (
                                            <a
                                                href={`https://maps.google.com/?q=${encodeURIComponent(r.address)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="rs-phone-action rs-phone-action--map"
                                                title={`Open ${r.address} in Maps`}
                                                aria-label={`Open ${r.address} in Maps`}
                                            >
                                                <Icons.MapPin size={13} aria-hidden="true" />
                                                <span>Map</span>
                                            </a>
                                        )}
                                        {(() => {
                                            const fd = r.q?.formData || r.q?.form_data || {};
                                            const phone = fd.clientPhone || fd.phone || fd.clientMobile;
                                            if (!phone) return null;
                                            return (
                                                <a
                                                    href={`tel:${phone}`}
                                                    className="rs-phone-action rs-phone-action--tel"
                                                    title={`Call ${phone}`}
                                                    aria-label={`Call ${phone}`}
                                                >
                                                    <Icons.Phone size={13} aria-hidden="true" />
                                                    <span>Call</span>
                                                </a>
                                            );
                                        })()}
                                        <span className="rs-card-chevron" aria-hidden="true">
                                            <Icons.ChevronRight size={14} />
                                        </span>
                                    </div>
                                )}
                            </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            </section>

            {previewRow && (
                <div
                    className="rs-preview-backdrop"
                    data-closing={closing ? 'true' : undefined}
                    onClick={closePreview}
                    aria-hidden="true"
                />
            )}

            {previewRow && (
                <aside
                    id="run-sheet-project-preview"
                    className="rs-preview"
                    data-closing={closing ? 'true' : undefined}
                    aria-labelledby="run-sheet-project-preview-title"
                >
                    <div
                        className="rs-preview__grab-bar"
                        aria-hidden="true"
                        onClick={closePreview}
                        title="Tap to close"
                    >
                        <div className="rs-preview__grab-handle" />
                    </div>
                    <div className="rs-preview__head">
                        <span className="rs-preview__lane" data-lane={previewRow.lane}>
                            {LANES.find(l => l.key === previewRow.lane)?.label || 'Project'}
                        </span>
                        <button
                            type="button"
                            className="rs-preview__close qm-inline-action-btn"
                            onClick={closePreview}
                            title="Close preview (Esc)"
                            aria-label="Close preview"
                        >
                            <Icons.X size={13} aria-hidden="true" />
                        </button>
                    </div>

                    {/* ── The masthead ────────────────────────────────────
                        Photo thumbnail, title, JOB-TYPE TAGS, then the objective.
                        The photo is a thumbnail rather than a backdrop because a
                        picture readable enough to be worth showing and a scrim
                        dark enough to guarantee contrast are opposites (operator,
                        2026-07-31: "the hero image isn't really visible"). It
                        stays a real <img> so it keeps `loading="lazy"` and
                        `decoding="async"` — full-resolution hero images decoding
                        synchronously at mount is a known 30-second main-thread
                        freeze in this app. With no photo the title simply sits
                        flush and nothing is indented around a gap. */}
                    {/* The hero comes from the per-id DETAIL cache, not the
                        summary row: it is a base64 data: URL and carrying it on
                        every summary made the Run Sheet's payload 19x larger
                        for an image at most one project ever shows. Until the
                        detail lands this reads null and the panel simply sits
                        flush, which is the same thing it does for a project
                        with no hero at all. */}
                    <div className="rs-hero" data-has-image={previewHeroCandidate ? 'true' : 'false'}>
                        <div className="rs-hero__top">
                            <RunSheetHeroThumbnail
                                candidate={previewHeroCandidate}
                                projectId={previewId}
                            />
                            {/* Title and JOB-TYPE TAGS share the column beside the
                                photo (operator, 2026-07-31: the tags "go under
                                the Quote/Job title in place of" the evidence
                                chips, which move down to the reference block).
                                They belong together — the tags say what KIND of
                                job this is, which is the natural second line
                                under its name — and they are still the verified
                                search terms, so each one doubles as a way back to
                                the job. Two short lines are also almost exactly
                                the thumbnail's height, so the block squares off
                                instead of leaving a notch under the picture. */}
                            <div className="rs-hero__ident">
                                <h2 id="run-sheet-project-preview-title" className="rs-preview__title">{previewRow.title}</h2>
                                <div className="rs-find__chips" role="group" aria-label="Job type — click to find similar jobs">
                                    {previewFindAgain.length === 0 ? (
                                        <span className="rs-find__none">No trade detected in this job’s scope yet</span>
                                    ) : previewFindAgain.map((t) => (
                                        <button
                                            key={t.key}
                                            type="button"
                                            className="rs-find__chip qm-inline-action-btn"
                                            data-kind={t.kind}
                                            onClick={() => { setQuery(t.term); setSearchHelperOpen(false); }}
                                            title={`Find jobs matching "${t.term}"`}
                                        >
                                            {/* Sentence-cased for display ONLY. The term
                                                itself is unchanged, and the matcher
                                                lowercases the query before comparing
                                                (history-search.ts), so case cannot alter
                                                what the chip returns. Without this a
                                                fallback to the bare category id renders
                                                as "concrete" beside "Concrete repair",
                                                which reads as a typo. */}
                                            {t.term.charAt(0).toUpperCase() + t.term.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="rs-hero__body">

                    {/* The panel earns its width by showing what the ROW CANNOT.
                        Client, address, SP, QR, value and stage are all already
                        columns, so repeating them is the whole panel saying
                        nothing. What is not on the sheet: what the work actually
                        is, who to ring, what evidence is on hand, and how far
                        down the line it has already gone. */}
                    {(() => {
                        const d = previewDetail.get(previewRow.id);
                        const objective = d?.objective || '';
                        const desc = objective || d?.jobDescription || '';
                        if (!d && previewLoading) return <p className="rs-preview__desc rs-preview__desc--loading">Loading the quote objective…</p>;
                        if (!desc) return null;
                        return (
                            <p className="rs-preview__desc">
                                {objective && <span className="rs-preview__desclabel">Objective</span>}
                                {desc}
                            </p>
                        );
                    })()}

                        </div>
                    </div>

                    {/* ── The controls, ABOVE the fold ────────────────────
                        Operator, 2026-07-31: "I want the CTA buttons like Open
                        quote (or Resume) and Delete not to be hidden and need to
                        scroll down, but be just under the ... top section."
                        They used to sit at the very bottom of the panel's
                        content behind `margin-top: auto`, so on a job with a
                        long objective and a full fact list the primary action
                        was below the fold — the one control the panel exists to
                        offer was the one you had to go looking for. They now sit
                        directly under the masthead, and the region BELOW them is
                        the only part that scrolls, so they cannot leave the
                        screen no matter how much detail a job carries.
                        This is also why opening is still a deliberate act: the
                        button is prominent, but it is a button, never the row. */}
                    <div className="rs-preview__actions">
                        {/* Express finish only where it can act: an imported
                            draft still sitting at Stage 1. Same guard the row
                            chip carried. */}
                        {previewRow.lane === 'finish' && previewRow.stage <= 1 && onGenerateQuote && (
                            <button
                                type="button"
                                className="rs-preview__btn qm-inline-action-btn"
                                onClick={() => onGenerateQuote(previewRow.q)}
                                title="Run the AI cascade from Stage 1"
                            >
                                <Icons.Zap size={13} aria-hidden="true" />
                                Generate
                            </button>
                        )}
                        <button
                            type="button"
                            className="rs-preview__btn rs-preview__btn--primary qm-inline-action-btn"
                            onClick={() => openProject(previewRow)}
                        >
                            {LANES.find(l => l.key === previewRow.lane)?.action || 'Open'}
                            <Icons.ArrowRight size={13} aria-hidden="true" />
                        </button>
                        {onDeleteProject && (
                            <button
                                type="button"
                                className="rs-preview__btn rs-preview__btn--danger qm-inline-action-btn"
                                onClick={() => onDeleteProject(previewRow.id)}
                                title={`Delete ${previewRow.title}`}
                                aria-label={`Delete ${previewRow.title}`}
                            >
                                <Icons.Trash size={13} aria-hidden="true" />
                            </button>
                        )}
                    </div>

                    {/* Everything below the actions is reference, and it is the
                        only part that scrolls. */}
                    <div className="rs-preview__scroll">
                    <dl className="rs-preview__facts">
                        {/* CLIENT FIRST (operator, 2026-07-31). The sheet's
                            CLIENT column is de-noised to fit 176px; here there
                            is room for the name as entered.
                            An honest distinction worth knowing: `clientCompany`
                            is what was typed on the JOB, and on the live corpus
                            that is usually the owners corporation ("The Owners -
                            Strata Plan 19180"), whereas `company_id` resolves to
                            the managing AGENCY ("Strata Partners", "PICA
                            Group"). They are different records, so the link is
                            labelled by what it opens rather than repeating the
                            name beside it.
                            Since 2026-10-08 (SPEC A2.3) clientCompany is the
                            firm only, so a linked job whose row names no firm
                            (owners wording only, or the offline summary with no
                            company_name) still gets the link, labelled by what
                            it opens. Owners wording is never the label. */}
                        {(previewRow.clientCompany || (previewRow.companyId && onNavigate)) && (
                            <><dt>Client</dt><dd>
                                {/* The NAME is the link (operator, 2026-07-31).
                                    A separate "View client" chip beside it read as
                                    a second thing to parse and needed spacing
                                    nobody could get right; the company name is
                                    already the noun you want to click. Falls back
                                    to plain text when the job has no linked client
                                    record, so a dead link is never rendered. */}
                                {previewRow.companyId && onNavigate ? (
                                    <button
                                        type="button"
                                        className="rs-preview__clientlink qm-inline-action-btn"
                                        /* TWO things here are load-bearing and both
                                           fail SILENTLY (verified live 2026-07-31 —
                                           the first cut of this button navigated to
                                           Clients and selected nothing):
                                           1. The payload MUST be nested under
                                              `pageNavOptions`. handleNavigate reads
                                              `options.pageNavOptions`, and a flat bag
                                              falls through to the `else` that clears
                                              stale options (use-app-routing.ts:617-621).
                                           2. `crmView` is NOT optional. ClientsPage
                                              only consumes a companyId hint while
                                              viewMode === 'requesters', and it clears
                                              the hint unconditionally either way
                                              (ClientsPage.jsx:2079-2083), so a missing
                                              view spends the hint on nothing. */
                                        onClick={() => onNavigate('clients', {
                                            pageNavOptions: {
                                                crmView: 'requesters',
                                                companyId: previewRow.companyId,
                                            },
                                        })}
                                        title="Open this job's client record on the Clients page"
                                    >
                                        {previewRow.clientCompany || 'Open client record'}
                                        <Icons.ArrowRight size={11} aria-hidden="true" />
                                    </button>
                                ) : previewRow.clientCompany}
                            </dd></>
                        )}
                        {previewRow.clientName && previewRow.clientName !== previewRow.clientCompany && (
                            <><dt>Attention</dt><dd>{previewRow.clientName}</dd></>
                        )}
                        {previewRow.clientEmail && (
                            <><dt>Email</dt><dd>
                                <a className="rs-preview__tel" href={`mailto:${previewRow.clientEmail}`}>
                                    {previewRow.clientEmail}
                                </a>
                            </dd></>
                        )}
                        {previewRow.qr && (
                            <>
                                <dt>Quote req</dt>
                                <dd className="rs-preview__mono" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    {previewRow.qr}
                                    {isRevisionProject(previewRow) && (
                                        <span style={{
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            padding: '1px 5px',
                                            borderRadius: '3px',
                                            background: '#e0f2fe',
                                            color: '#0369a1',
                                        }}>
                                            {getRevisionBadgeText(previewRow)}
                                        </span>
                                    )}
                                </dd>
                            </>
                        )}
                        {previewRow.contactName && (
                            <><dt>Ring</dt><dd>
                                {previewRow.contactName}
                                {previewRow.contactPhone && (
                                    <a className="rs-preview__tel" href={`tel:${String(previewRow.contactPhone).replace(/\s+/g, '')}`}>
                                        {previewRow.contactPhone}
                                    </a>
                                )}
                            </dd></>
                        )}
                        {!previewRow.contactName && previewRow.contactPhone && (
                            <><dt>Ring</dt><dd><a className="rs-preview__tel" href={`tel:${String(previewRow.contactPhone).replace(/\s+/g, '')}`}>{previewRow.contactPhone}</a></dd></>
                        )}
                        {previewRow.visitDate && (<><dt>Site visit</dt><dd className="rs-preview__mono">{previewRow.visitDate}</dd></>)}
                        {previewRow.address && (<><dt>Address</dt><dd>{previewRow.address}{previewRow.unitNumber ? `, unit ${previewRow.unitNumber}` : ''}</dd></>)}
                        <dt>Value</dt>
                        <dd className="rs-preview__mono">
                            {previewRow.value < 1 ? 'Not priced yet' : money0(previewRow.value) + ' inc GST'}
                        </dd>
                        <dt>Idle</dt>
                        <dd className="rs-preview__mono" data-tone={ageTone(previewRow.days)}>
                            {previewRow.days === 0 ? 'Moved today' : `${previewRow.days} day${previewRow.days === 1 ? '' : 's'}`}
                        </dd>
                    </dl>

                    {/* How far it has actually gone. Each step is either done or
                        it is not, which is the question the lane chip only
                        answers approximately. */}
                    <div className="rs-preview__track">
                        <span className="rs-stage rs-preview__stage" title={stageTitle(previewRow.stage)}>
                            {[1, 2, 3, 4, 5].map(n => (
                                <i key={n} data-on={n <= previewRow.stage ? 'true' : 'false'} aria-hidden="true" />
                            ))}
                            <span className="rs-stage__n">{previewRow.stage}/5</span>
                        </span>
                        {[
                            { on: previewRow.quoteComplete, label: 'Quote built' },
                            { on: previewRow.pdfGenerated, label: 'PDF made' },
                            { on: previewRow.emailSent, label: 'Sent' },
                        ].map(({ on, label }) => (
                            <span key={label} className="rs-milestone" data-on={on ? 'true' : 'false'}>
                                {on ? <Icons.Check size={11} aria-hidden="true" /> : null}
                                {label}
                            </span>
                        ))}
                        {/* Only the three outcomes that are DECISIONS. The other
                            values getQuoteOutcome returns (sent / quoted /
                            draft) are progress, which the milestones beside
                            this already say — tsc caught the original guard
                            comparing against a 'pending' that does not exist,
                            which would have stamped a chip on every project. */}
                        {(previewRow.outcome === 'won' || previewRow.outcome === 'lost' || previewRow.outcome === 'expired') && (
                            <span className="rs-milestone rs-milestone--outcome" data-outcome={previewRow.outcome}>
                                {previewRow.outcome.toUpperCase()}
                            </span>
                        )}
                    </div>

                    {/* ── Review label ───────────────────────────────────
                        Deliberately NOT inside `rs-preview__actions`. That band
                        is the fixed above-the-fold home of Generate / Open /
                        Delete, and the swallow test slices it at +2200
                        characters with almost no headroom left — an insert
                        there pushes onGenerateQuote out of the window it is
                        pinned in. It also does not belong beside them: those
                        three move a job forward, this one annotates it.

                        Placed after the milestone track and before the
                        reference files, which is where the panel stops saying
                        "here is what the app knows" and starts saying "here is
                        what you can do about it". */}
                    <div className="rs-find">
                        <span className="rs-find__label">Review label</span>
                        <div className="rs-find__chips">
                            <ReviewLabelPicker
                                labels={labels}
                                value={previewRow.labelId}
                                heading="Mark this job"
                                onPick={(id) => applyLabel([previewRow.id], id)}
                                onManage={onOpenSettings ? openLabelSettings : undefined}
                                onCreate={(input) => createAndApply(input, [previewRow.id])}
                                createTargetCount={1}
                                onRename={renameLabel}
                                onDelete={deleteLabel}
                                onRefreshLabels={refreshLabels}
                                triggerClassName="rs-find__chip qm-inline-action-btn"
                                triggerKind="label"
                                triggerTitle="Change the review label on this job"
                            >
                                <Icons.Tag size={12} aria-hidden="true" />
                                {previewRow.labelName || 'Add label'}
                            </ReviewLabelPicker>
                            {/* Clearing acts immediately, with no confirm. A
                                label is trivially re-appliable, so a dialog
                                here would be friction rather than safety. */}
                            {previewRow.labelId && (
                                <button
                                    type="button"
                                    className="rs-find__chip qm-inline-action-btn"
                                    data-kind="file"
                                    onClick={() => applyLabel([previewRow.id], null)}
                                    title="Take the review label off this job"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Reference files ────────────────────────────────
                        Operator, 2026-07-31: the evidence chips move down here,
                        "and also have a button to preview the reference files
                        starting with the Quote Request (RFQ pdf)".
                        The counts and the way IN to the files belong together:
                        "3 documents" was previously a fact you could read and
                        not act on. */}
                    <div className="rs-find">
                        <span className="rs-find__label">Reference files</span>
                        <div className="rs-preview__evidence">
                            {[
                                { n: previewRow.photoCount, label: 'photo', icon: Icons.Image },
                                { n: previewRow.documentCount, label: 'document', icon: Icons.FileText },
                                { n: previewRow.videoCount, label: 'video', icon: Icons.Video },
                                { n: previewRow.scopeCount, label: 'scope line', icon: Icons.List },
                            ].map(({ n, label, icon: Ico }) => (
                                <span key={label} className="rs-ev" data-empty={n ? 'false' : 'true'}>
                                    {Ico ? <Ico size={12} aria-hidden="true" /> : null}
                                    <b>{n || 0}</b> {label}{n === 1 ? '' : 's'}
                                </span>
                            ))}
                        </div>

                        {/* ONE CLICK to the quote request (operator: "I want to
                            be able to preview the Quote Request PDF from a
                            single click"). It costs no extra request: the panel
                            already fetches the full project record for the
                            objective, and `documents` rides along in that same
                            response — it was simply being discarded.
                            The RFQ is identified by the shipped classifier's
                            own verdict via resolveDocumentClassification, not by
                            a filename guess here, so this button agrees with
                            every other surface that talks about document class. */}
                        {previewFiles.loading && <p className="rs-find__hint">Looking for the reference files…</p>}
                        {!previewFiles.loading && previewFiles.documents.length === 0 && (
                            <p className="rs-find__hint">No documents are attached to this job yet.</p>
                        )}
                        {!previewFiles.loading && previewFiles.documents.length > 0 && (
                            <div className="rs-find__chips">
                                <button
                                    type="button"
                                    className="rs-find__chip rs-find__chip--file qm-inline-action-btn"
                                    data-kind={previewFiles.rfq ? 'rfq' : 'file'}
                                    onClick={() => setFilesOpen(true)}
                                    title={previewFiles.rfq
                                        ? `Preview ${previewFiles.rfq.name}`
                                        : 'Preview the documents attached to this job'}
                                >
                                    <Icons.FileText size={12} aria-hidden="true" />
                                    {previewFiles.rfq ? 'Quote request' : 'Open documents'}
                                </button>
                                {/* Named honestly: when no document classifies as
                                    an RFQ the button does not pretend one exists,
                                    it just opens what IS there. */}
                                {previewFiles.rfq && previewFiles.documents.length > 1 && (
                                    <button
                                        type="button"
                                        className="rs-find__chip rs-find__chip--file qm-inline-action-btn"
                                        data-kind="file"
                                        onClick={() => setFilesOpen(true)}
                                        title="Preview every document attached to this job"
                                    >
                                        All {previewFiles.documents.length} documents
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {previewRow.awaitsReview && (
                        <p className="rs-preview__flag">
                            Drafted by QuoteMaker and not yet reviewed. Open it at Stage 5 and save to clear this.
                        </p>
                    )}
                    </div>

                    {/* The viewer is ReferenceFilesPanel, not a new one. It
                        already owns the PDF / DOCX / image / email routing, the
                        signed-URL re-mint on expiry, and its own lightbox — a
                        second implementation would drift from it within a
                        release. It is lazy so the Run Sheet does not carry a PDF
                        renderer in its bundle for a panel most opens never use. */}
                    {filesOpen && (
                        <div
                            className="rs-files"
                            role="dialog"
                            aria-modal="true"
                            aria-label={`Reference files for ${previewRow.title}`}
                            onClick={() => setFilesOpen(false)}
                        >
                            <div className="rs-files__card" onClick={(e) => e.stopPropagation()}>
                                <div className="rs-files__head">
                                    <span className="rs-files__title">{previewRow.title}</span>
                                    <button
                                        type="button"
                                        className="rs-preview__close qm-inline-action-btn"
                                        onClick={() => setFilesOpen(false)}
                                        aria-label="Close reference files"
                                        title="Close (Esc)"
                                    >
                                        <Icons.X size={13} aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="rs-files__body">
                                    <React.Suspense fallback={<p className="rs-find__hint">Loading the file viewer…</p>}>
                                        <LazyReferenceFilesPanel
                                            documents={previewFiles.documents}
                                            projectRecord={{ id: previewRow.id, title: previewRow.title }}
                                            showHeader={false}
                                            expandableDocuments
                                            maxDocuments={50}
                                        />
                                    </React.Suspense>
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
            )}
            </div>
            {/* Label writes are optimistic, so the toast is the ONLY channel
                that reports a failed one. Without it a reverted chip would just
                blink back to its old value and read as a rendering glitch. */}
            <ToastBar />
        </div>
    );
}
